import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import SearchInterface from '../components/search/SearchInterface';
import { FiltersState } from '../hooks/useFilters';
import { extractLastPathSegment } from '../utils/filterUtils';
import { RelevanceFilter } from '../components/search/RelevanceFilters';
import SearchInfoCard from '../relevanter/components/SearchInfoCard';
import ResultsCard from '../relevanter/components/ResultsCard';
import AdminPanelCard from '../relevanter/components/AdminPanelCard';
import HHSettingsCard from '../relevanter/components/HHSettingsCard';
import StatisticsCard from '../relevanter/components/StatisticsCard';
import ActivityCard from '../relevanter/components/ActivityCard';
import HHAuthCard from '../relevanter/components/HHAuthCard';
import EStaffAuthCard from '../relevanter/components/EStaffAuthCard';
import VacanciesCard from '../relevanter/components/VacanciesCard';
import ResponsesTab from '../relevanter/components/ResponsesTab';
import SearchFromSourcesHeader from '../relevanter/components/SearchFromSourcesHeader';
import SourceTabs from '../relevanter/components/SourceTabs';
import { ContentTabToggle } from '../components/ui/content-tab-toggle';
import { useAuthStore } from '../store/authStore';
import { useVacancyStore } from '../store/vacancyStore';
import { searchApplicants, searchHHWithRelevance, searchEStaffWithRelevance, searchAvitoResumes, checkHHStatus, getHHVacancyById, evaluateResume, ResponsesFilters, getBatchVacancyResponsesPaged, findRelevantVacancyResponses, cancelSearchSession, getSources, getAvitoVacancyApplications, AvitoVacancyApplicationsFilters } from '../relevanter/services/api';
import {
  ALL_AVITO_APPLICATION_STATES,
  DEFAULT_AVITO_APPLICATION_STATE,
  useResponsesFilters,
} from '../hooks/useResponsesFilters';
import { createApiQuery } from '../utils/filterUtils';
import { needsPostFilter } from '../relevanter/utils/needsPostFilter';
import { parseSkillGroupsRaw } from '../utils/skillGroupsParse';
import { compressCityIds, getAreaIdByName, expandSpecializationIds } from '../services/hhDictionariesService';
import { getCityIdByName } from '../data/hhCities';
import { isAdminOrAbove } from '../utils/roleUtils';
import { useSearchState } from '../relevanter/hooks/useSearchState';
import { useScrollPosition } from '../relevanter/hooks/useScrollPosition';
import { useVacancyStateCache } from '../store/vacancyStateCache';
import ScrollToTopButton from '../components/ui/ScrollToTopButton';
import { useSearchProgress, SearchProgress } from '../relevanter/hooks/useSearchProgress';
import { useBrandingColors } from '../hooks/useBrandingColors';
import { buildFiltersForSourceSwitch, getSerializableFiltersForSource, sanitizeFiltersForSource } from '../sources/filterPolicy';
import { applySourceVacancyFilters, hasSourceVacancyMapper } from '../sources/sourceModules';
import { buildVacancyDataHash, getVacancySpecializationsForSource } from './RelevanterPage.utils';
import { isSameHhStoredSpecializationSelection } from '../sources/hh/specializationSelection';
import { CandidateSourceAvailability, getAvailableSourceIds, normalizeSourceId, resolveAvailableSource } from '../relevanter/utils/sourceAvailability';
import toast from 'react-hot-toast';

// Тип для viewMode
type RelevanterViewMode = 
  | "search"           // Основное состояние с поиском
  | "admin-panel"      // Админ панель
  | "hh-settings"      // Настройки HH (устарело, перенаправляем на hh-auth)
  | "statistics"       // Статистика
  | "activity"         // Активность
  | "hh-auth"          // Авторизация HH
  | "ats-settings"     // Настройки ATS
  | "vacancies";       // Список вакансий

// Structured logging
const logInfo = (message: string, data?: any) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`[RELEVANTER] ${message}`, data || '');
  }
};

const logError = (message: string, error?: any) => {
  console.error(`[RELEVANTER] ERROR: ${message}`, error || '');
};

const getFiltersStateKey = (filters: Partial<FiltersState>): string => {
  const source = filters.source || 'hh';
  return JSON.stringify(getSerializableFiltersForSource(source, filters));
};

const hasAvailableSource = (
  sources: CandidateSourceAvailability[] | null,
  sourceId: string,
): boolean => (
  getAvailableSourceIds(sources).includes(normalizeSourceId(sourceId))
);

const setSourceAvailable = (
  sources: CandidateSourceAvailability[],
  sourceId: string,
  available: boolean,
): CandidateSourceAvailability[] => (
  sources.map((source) => (
    normalizeSourceId(source.id) === normalizeSourceId(sourceId)
      ? { ...source, available }
      : source
  ))
);

// Утилита для очистки HTML
const stripHtml = (html: string): string => {
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

// Маппинг опыта из формата вакансии в формат HH фильтров
const mapExperienceToHH = (experience: string): string => {
  const experienceMap: Record<string, string> = {
    'Нет опыта': 'noExperience',
    'От 1 года до 3 лет': 'between1And3',
    'От 3 до 6 лет': 'between3And6',
    'Более 6 лет': 'moreThan6',
  };
  return experienceMap[experience] || '';
};

// Маппинг периода опыта
const mapExperiencePeriodToHH = (period: string): string => {
  const hhValues = ['all_time', 'last_year', 'last_three_years', 'last_six_years'];
  if (hhValues.includes(period)) {
    return period;
  }
  const periodMap: Record<string, string> = {
    'Нет опыта': 'last_year',
    'От 1 года до 3 лет': 'last_three_years',
    'От 3 до 6 лет': 'last_six_years',
    'Более 6 лет': 'all_time',
  };
  return periodMap[period] || '';
};


// Маппинг статуса кандидата из формата вакансии в формат HH фильтров
const mapCandidateStatusToHH = (status: string): string => {
  const statusMap: Record<string, string> = {
    'actively_searching': 'active_search',
    'considering_offers': 'looking_for_offers',
    'deciding': 'has_job_offer',
    'started_new_job': 'accepted_job_offer',
    'not_searching': 'not_looking_for_job',
  };
  return statusMap[status] || '';
};

// Create a new query client for Relevanter
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

interface SearchData {
  items: any[];
  count: number;
  estimatedFilteredTotal?: number;
  totalPages?: number | null;
  isEstimated?: boolean;
  totalFound?: number;
  filteredCount?: number;
  scoringApplied?: boolean;
  hhCursor?: number; // HH-страница на которой остановился бекенд (для дозагрузки)
  hhExhausted?: boolean; // HH-страницы закончились
  avitoCursor?: number; // Avito-страница, с которой продолжит бекенд при пост-фильтре
  avitoExhausted?: boolean;
  isApproximate?: boolean; // число результатов приблизительное (при пост-фильтрах)
  jumpersFiltered?: number; // сколько джамперов отфильтровано
  industryFiltered?: number; // сколько отфильтровано по отрасли
}

const getSearchInterfaceCandidateCount = (data: SearchData | null): number => {
  if (!data) return 0;

  const itemsCount = Array.isArray(data.items) ? data.items.length : 0;
  if (Number(data.totalPages) === 1) {
    return itemsCount;
  }

  const candidateCount = Number(data.estimatedFilteredTotal ?? data.count ?? itemsCount);
  return Number.isFinite(candidateCount) ? candidateCount : itemsCount;
};

type VacancyFiltersSnapshot = Partial<FiltersState> & {
  searchMode: 'custom' | 'vacancy';
  vacancyDescription: string;
  atsVacancyId: string;
  atsVacancyTitle: string;
  positionName: string;
  expectedPositionName: string;
  cities: string[];
  cityIds: string[];
  skillsAny: string[];
  salaryFrom: string;
  salaryTo: string;
  salaryCurrency: string;
  incomeSpecified: boolean;
  hasSalary: boolean;
  positionExperience: string;
  experiencePeriod: string;
  totalExperience: string;
  excludeJumpers: boolean;
  filterByExpIndustry: boolean;
  education: string[];
  selectedIndustries: string[];
  selectedSpecializations: string[];
  jobSearchStatus: string[];
  gender: string;
  ageFrom: string;
  ageTo: string;
  citizenship: string[];
  metro: string | string[];
  metroCityId: string;
  workFormat: string;
  strictRegionSearch: boolean;
};

const RelevanterPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user, role } = useAuthStore();
  const {
    selectedVacancy,
    selectedVacancyId,
    selectedVacancyLoading,
    refreshSelectedVacancy
  } = useVacancyStore();
  const canAccessAdminViews = role ? isAdminOrAbove(role as any) : false;
  const activeAdminView = new URLSearchParams(location.search).get('admin');
  const isHhAuthRoute = activeAdminView === 'settings-hh'
    || (canAccessAdminViews && activeAdminView === 'settings');
  const colors = useBrandingColors();
  const [filtersVisible, setFiltersVisible] = useState<boolean>(true);
  const [parsedSummary, setParsedSummary] = useState<any>(null);

  const lastRefreshAttemptVacancyIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (!selectedVacancyId) return;
    if (selectedVacancyLoading) return;
    if (lastRefreshAttemptVacancyIdRef.current === selectedVacancyId) return;
    if ((selectedVacancy as any)?.skillGroups) return;
    lastRefreshAttemptVacancyIdRef.current = selectedVacancyId;
    refreshSelectedVacancy();
  }, [selectedVacancyId, refreshSelectedVacancy, selectedVacancy, selectedVacancyLoading]);

  // Получаем параметры из выбранной вакансии (vacancyStore) вместо URL
  const getVacancyFilters = (source = 'hh'): VacancyFiltersSnapshot => {
    if (!selectedVacancy) {
      return {
        searchMode: 'custom',
        vacancyDescription: '',
        atsVacancyId: '',
        atsVacancyTitle: '',
        positionName: '',
        expectedPositionName: '',
        cities: [] as string[],
        cityIds: [] as string[],
        skillsAny: [] as string[],
        salaryFrom: '',
        salaryTo: '',
        salaryCurrency: 'RUR',
        incomeSpecified: false,
        hasSalary: false,
        positionExperience: '',
        experiencePeriod: '',
        totalExperience: '',
        excludeJumpers: false,
        filterByExpIndustry: false,
        education: [],
        selectedIndustries: [] as string[],
        selectedSpecializations: [] as string[],
        jobSearchStatus: [] as string[],
        gender: '',
        ageFrom: '',
        ageTo: '',
        citizenship: [] as string[],
        metro: '',
        metroCityId: '',
        workFormat: '',
        strictRegionSearch: false,
      };
    }

    // Очищаем HTML из описания
    const cleanDescription = selectedVacancy.description
      ? stripHtml(selectedVacancy.description)
      : '';

    // Формируем полный текст вакансии
    const fullVacancyText = [
      selectedVacancy.title || '',
      cleanDescription,
      selectedVacancy.topics?.join(', ') || ''
    ].filter(Boolean).join('\n\n');

    // Маппинг статусов кандидатов
    const mappedStatuses = (selectedVacancy.candidateStatuses || [])
      .map(mapCandidateStatusToHH)
      .filter(Boolean);

    // Города из вакансии теперь хранятся как ID
    // Конвертируем старые названия в ID если нужно (обратная совместимость)
    const rawCities = selectedVacancy.cities || [];
    const cityIds = rawCities.map((city: string) => {
      // Если уже ID (числовая строка) - оставляем
      if (/^\d+$/.test(city)) {
        return city;
      }
      // Пробуем конвертировать название в ID
      const id = getCityIdByName(city);
      return id || city;
    });

    const sourceOwnsVacancyMapping = hasSourceVacancyMapper(source);
    const backendSpecializations = getVacancySpecializationsForSource(selectedVacancy as any, source);

    let restoredSpecializations = backendSpecializations;
    if (!sourceOwnsVacancyMapping && selectedVacancyId) {
      try {
        const key = `vacancySpecializationPathKeys_${selectedVacancyId}`;
        const raw = localStorage.getItem(key);
        const cached = raw ? JSON.parse(raw) : null;
        const cachedArr = Array.isArray(cached) ? cached.map(String).filter(Boolean) : [];
        if (isSameHhStoredSpecializationSelection(backendSpecializations, cachedArr)) {
          restoredSpecializations = cachedArr;
        }
      } catch {}
    }

    const citizenshipValues = Array.isArray((selectedVacancy as any)?.citizenship)
      ? (selectedVacancy as any).citizenship.map(String).filter(Boolean)
      : typeof (selectedVacancy as any)?.citizenship === 'string' && (selectedVacancy as any).citizenship.trim()
      ? (selectedVacancy as any).citizenship.split(',').map((value: string) => value.trim()).filter(Boolean)
      : [];

    const baseVacancyFilters: VacancyFiltersSnapshot = {
      searchMode: 'vacancy',
      vacancyDescription: fullVacancyText,
      atsVacancyId: '',
      atsVacancyTitle: '',
      positionName: selectedVacancy.title || '',
      // TASKNEIROKLYUCH-790: вакансия не несёт «предполагаемой должности»
      // (это поле резюме кандидата). Дефолт '' нужен для инлайнового типа,
      // который читается в кеш-ключе useResponsesFilters ниже.
      expectedPositionName: '',
      cities: [] as string[], // Пустой - используем cityIds
      cityIds,
      metro: (selectedVacancy as any)?.useMetroInSearch && Array.isArray((selectedVacancy as any)?.officeLocations) && (selectedVacancy as any).officeLocations.length > 0
        ? (selectedVacancy as any).officeLocations.map((loc: any) => loc.metroStationId).filter(Boolean)
        : '',
      metroCityId: (selectedVacancy as any)?.officeCityId ? String((selectedVacancy as any).officeCityId) : '',
      workFormat: (selectedVacancy as any)?.workFormat || '',
      skillsAny: selectedVacancy.topics || [],
      skillGroups: parseSkillGroupsRaw((selectedVacancy as any).skillGroups) as any,
      salaryFrom: selectedVacancy.salaryFrom ? String(selectedVacancy.salaryFrom) : '',
      salaryTo: selectedVacancy.salaryTo ? String(selectedVacancy.salaryTo) : '',
      salaryCurrency: selectedVacancy.salaryCurrency || 'RUR',
      incomeSpecified: selectedVacancy.salarySpecified || false,
      hasSalary: selectedVacancy.salarySpecified || false,
      positionExperience: selectedVacancy.experienceInPosition
        ? selectedVacancy.experienceInPosition.split(',').map(exp => mapExperienceToHH(exp.trim())).filter(Boolean).join(',')
        : '',
      experiencePeriod: selectedVacancy.experiencePeriod
        ? mapExperiencePeriodToHH(selectedVacancy.experiencePeriod)
        : '',
      totalExperience: selectedVacancy.totalExperience
        ? selectedVacancy.totalExperience.split(',').map(exp => mapExperienceToHH(exp.trim())).filter(Boolean).join(',')
        : '',
      excludeJumpers: Boolean((selectedVacancy as any).excludeJumpers),
      filterByExpIndustry: Boolean((selectedVacancy as any).filterByExpIndustry),
      scoringEnabled: (selectedVacancy as any)?.scoringEnabled !== false,
      education: selectedVacancy.education ? String(selectedVacancy.education).split(',').map((s: string) => s.trim()).filter(Boolean) : [],
      selectedIndustries: selectedVacancy.industry
        ? (Array.isArray(selectedVacancy.industry) 
            ? selectedVacancy.industry.filter(Boolean).map(String)
            : typeof selectedVacancy.industry === 'string' && selectedVacancy.industry.trim()
            ? selectedVacancy.industry.split(',').map((s: string) => s.trim()).filter(Boolean).map(String)
            : [])
        : [],
      selectedSpecializations: restoredSpecializations,
      jobSearchStatus: mappedStatuses,
      gender: selectedVacancy.gender === 'male' ? 'M' : selectedVacancy.gender === 'female' ? 'F' : '',
      ageFrom: selectedVacancy.ageFrom ? String(selectedVacancy.ageFrom) : '',
      ageTo: selectedVacancy.ageTo ? String(selectedVacancy.ageTo) : '',
      citizenship: citizenshipValues,
      strictRegionSearch: (selectedVacancy as any)?.strictRegionSearch ?? false,
    };

    return (sourceOwnsVacancyMapping
      ? applySourceVacancyFilters(source, baseVacancyFilters as any, {
        vacancy: selectedVacancy as any,
        vacancyId: selectedVacancyId as any,
      })
      : baseVacancyFilters) as VacancyFiltersSnapshot;
  };

  const vacancyFilters = getVacancyFilters('hh');
  const vacancyTitle = selectedVacancy?.title || '';
  const sourcePositionId = selectedVacancyId;

  // Лог при изменении выбранной вакансии
  useEffect(() => {
    logInfo('Vacancy context', {
      hasVacancy: !!selectedVacancy,
      vacancyId: selectedVacancyId,
      vacancyTitle: selectedVacancy?.title || 'none',
      searchMode: vacancyFilters.searchMode
    });
  }, [selectedVacancy, selectedVacancyId]);

  // Инициализируем фильтры через useSearchState с данными из vacancyStore
  const defaultFilters: FiltersState = {
    source: 'hh',
    limit: 20,
    sortBy: 'relevance',
    page: 1,
    pageSize: 20,
    excludeUpdatedPeriod: '',
    salaryFrom: vacancyFilters.salaryFrom,
    salaryTo: vacancyFilters.salaryTo,
    incomeSpecified: vacancyFilters.incomeSpecified,
    hasSalary: vacancyFilters.hasSalary,
    education: Array.isArray(vacancyFilters.education) ? vacancyFilters.education : (vacancyFilters.education ? [String(vacancyFilters.education)] : []),
    cities: vacancyFilters.cities,
    cityIds: vacancyFilters.cityIds,
    ageFrom: vacancyFilters.ageFrom,
    ageTo: vacancyFilters.ageTo,
    includeNoAge: true,
    gender: vacancyFilters.gender,
    citizenship: vacancyFilters.citizenship,
    stopCompanies: [],
    donorCompanies: [],
    companyFilterMode: 'current',
    vacancyDescription: vacancyFilters.vacancyDescription,
    atsVacancyId: vacancyFilters.atsVacancyId,
    atsVacancyTitle: vacancyFilters.atsVacancyTitle,
    positionName: vacancyFilters.positionName,
    experiencePeriod: vacancyFilters.experiencePeriod,
    skillsAny: (vacancyFilters as any).skillGroups ? [] : vacancyFilters.skillsAny,
    skillsAll: [],
    skillGroups: (vacancyFilters as any).skillGroups,
    totalExperience: vacancyFilters.totalExperience,
    excludeJumpers: vacancyFilters.excludeJumpers,
    filterByExpIndustry: vacancyFilters.filterByExpIndustry,
    scoringEnabled: (vacancyFilters as any).scoringEnabled,
    positionExperience: vacancyFilters.positionExperience,
    jobSearchStatus: vacancyFilters.jobSearchStatus,
    selectedIndustries: vacancyFilters.selectedIndustries,
    selectedSpecializations: vacancyFilters.selectedSpecializations,
    strictRegionSearch: vacancyFilters.strictRegionSearch,
    metro: vacancyFilters.metro,
    metroCityId: vacancyFilters.metroCityId,
    workFormat: vacancyFilters.workFormat,
    avitoKeywords: (vacancyFilters as any).avitoKeywords || [],
  };
  const [searchSources, setSearchSources] = useState<CandidateSourceAvailability[] | null>(null);
  const [searchSourcesLoaded, setSearchSourcesLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSearchSourcesLoaded(false);
    if (!token) {
      setSearchSources(null);
      return () => {
        cancelled = true;
      };
    }

    getSources()
      .then(async (data: { sources?: CandidateSourceAvailability[] }) => {
        if (cancelled) return;
        let nextSources = Array.isArray(data?.sources) ? data.sources : [];
        if (!isHhAuthRoute && hasAvailableSource(nextSources, 'hh')) {
          try {
            const status = await checkHHStatus();
            if (cancelled) return;
            if (status?.status !== 'authorized') {
              nextSources = setSourceAvailable(nextSources, 'hh', false);
            }
          } catch {
            if (cancelled) return;
          }
        }
        setSearchSources(nextSources);
        setSearchSourcesLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setSearchSources([]);
        setSearchSourcesLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [token, isHhAuthRoute]);

  const availableSourceIds = useMemo(
    () => getAvailableSourceIds(searchSources),
    [searchSources],
  );

  const {
    filters,
    setFilters,
    cachedResultsPayload,
    cacheResults,
    getSavedFiltersForSource: getPersistedFiltersForSource,
    saveFiltersForSource: savePersistedFiltersForSource,
  } =
    useSearchState(defaultFilters as any, selectedVacancyId as any, {
      canSyncUrlForSource: (source: string) => (
        searchSourcesLoaded
        && availableSourceIds.includes(normalizeSourceId(source))
      ),
    }) as any;

  const resolvedSearchSource = useMemo(
    () => resolveAvailableSource(filters.source || 'hh', searchSources),
    [filters.source, searchSources],
  );
  const normalizedCurrentSource = normalizeSourceId(filters.source || 'hh');
  const sourceNeedsNormalization =
    searchSourcesLoaded
    && availableSourceIds.length > 0
    && !!resolvedSearchSource
    && resolvedSearchSource !== normalizedCurrentSource;
  const hasNoAvailableSearchSources = searchSourcesLoaded && availableSourceIds.length === 0;

  useEffect(() => {
    if (!searchSourcesLoaded || availableSourceIds.length > 0) return;
    const params = new URLSearchParams(location.search);
    if (!params.has('source')) return;
    params.delete('source');
    navigate({
      pathname: '/recruiter/search',
      search: params.toString() ? `?${params.toString()}` : '',
    }, { replace: true });
  }, [availableSourceIds.length, location.search, navigate, searchSourcesLoaded]);

  // Per-source хранение фильтров: при переключении источника сохраняем/восстанавливаем
  const sourceFiltersRef = useRef<Record<string, any>>({});

  // Режим поиска определяется наличием выбранной вакансии
  const [searchMode, setSearchMode] = useState(vacancyFilters.searchMode);

  // Flag for auto-parsing when coming from vacancy page
  const shouldAutoParse = vacancyFilters.searchMode === 'vacancy' && vacancyFilters.vacancyDescription.length > 0;

  const getVacancyDataHash = useCallback(() => {
    return buildVacancyDataHash(selectedVacancy as any);
  }, [selectedVacancy]);

  const prevVacancyHashRef = useRef<string>('');
  const lastSyncedVacancyIdRef = useRef<number | null>(null);
  const vacancyFiltersJustAppliedRef = useRef<{ vacancyId: number | null; filtersKey: string } | null>(null);
  useEffect(() => {
    if (selectedVacancyLoading) return;
    if (!selectedVacancy) return;
    if (selectedVacancyId && selectedVacancy.id !== selectedVacancyId) return;

    const currentHash = getVacancyDataHash();
    const isNewVacancySelection = lastSyncedVacancyIdRef.current !== selectedVacancyId;
    const activeVacancyFilters = getVacancyFilters(filters.source || 'hh');
    const activeAvitoKeywords = (activeVacancyFilters as any).avitoKeywords;
    const avitoKeywordsDiffer = (
      (filters.source || 'hh') === 'avito'
      && Array.isArray(activeAvitoKeywords)
      && activeAvitoKeywords.length > 0
      && JSON.stringify(activeAvitoKeywords) !== JSON.stringify(((filters as any).avitoKeywords || []))
    );
    
    // Check if we already have this vacancy state synced in storage
    if (selectedVacancyId) {
        const hashKey = `vacancyHash_v${selectedVacancyId}`;
        const savedHash = localStorage.getItem(hashKey);
        
        if (savedHash === currentHash && !isNewVacancySelection) {
             // Хэш совпал — но проверяем, не потерялись ли metro из-за старого кеша
             const cachedMetro = filters?.metro;
             const vacancyMetro = activeVacancyFilters.metro;
             const metroDiffers = (() => {
               if (!vacancyMetro && !cachedMetro) return false;
               if (!vacancyMetro || !cachedMetro) return true;
               return JSON.stringify(vacancyMetro) !== JSON.stringify(cachedMetro);
             })();
             if (!metroDiffers && !avitoKeywordsDiffer) {
               prevVacancyHashRef.current = currentHash;
               return;
             }
        }
    }
    
    if (prevVacancyHashRef.current === currentHash && !isNewVacancySelection && !avitoKeywordsDiffer) {
      return;
    }
    prevVacancyHashRef.current = currentHash;
    lastSyncedVacancyIdRef.current = selectedVacancyId ?? null;

    if (selectedVacancy) {
      logInfo('Updating filters from vacancy', { vacancyId: selectedVacancyId, title: selectedVacancy.title });
      const nextFilters = sanitizeFiltersForSource(filters.source || 'hh', {
        ...filters,
        salaryFrom: activeVacancyFilters.salaryFrom,
        salaryTo: activeVacancyFilters.salaryTo,
        salaryCurrency: activeVacancyFilters.salaryCurrency || 'RUR',
        incomeSpecified: activeVacancyFilters.incomeSpecified,
        hasSalary: activeVacancyFilters.hasSalary,
        education: Array.isArray(activeVacancyFilters.education) ? activeVacancyFilters.education : (activeVacancyFilters.education ? [String(activeVacancyFilters.education)] : []),
        cities: activeVacancyFilters.cities,
        cityIds: activeVacancyFilters.cityIds,
        ageFrom: activeVacancyFilters.ageFrom,
        ageTo: activeVacancyFilters.ageTo,
        gender: activeVacancyFilters.gender,
        citizenship: activeVacancyFilters.citizenship,
        vacancyDescription: activeVacancyFilters.vacancyDescription,
        atsVacancyId: activeVacancyFilters.atsVacancyId,
        atsVacancyTitle: activeVacancyFilters.atsVacancyTitle,
        positionName: activeVacancyFilters.positionName,
        experiencePeriod: activeVacancyFilters.experiencePeriod,
        skillsAny: (activeVacancyFilters as any).skillGroups ? [] : activeVacancyFilters.skillsAny,
        skillGroups: (activeVacancyFilters as any).skillGroups,
        totalExperience: activeVacancyFilters.totalExperience,
        excludeJumpers: activeVacancyFilters.excludeJumpers,
        filterByExpIndustry: activeVacancyFilters.filterByExpIndustry,
        scoringEnabled: (activeVacancyFilters as any).scoringEnabled,
        positionExperience: activeVacancyFilters.positionExperience,
        jobSearchStatus: activeVacancyFilters.jobSearchStatus,
        industry: '',
        specialization: '',
        selectedIndustries: activeVacancyFilters.selectedIndustries,
        selectedSpecializations: activeVacancyFilters.selectedSpecializations,
        metro: activeVacancyFilters.metro,
        metroCityId: activeVacancyFilters.metroCityId,
        workFormat: activeVacancyFilters.workFormat,
        strictRegionSearch: activeVacancyFilters.strictRegionSearch,
        ...(Array.isArray(activeAvitoKeywords) && activeAvitoKeywords.length > 0
          ? { avitoKeywords: activeAvitoKeywords }
          : {}),
        searchMode: 'vacancy',
        page: 1,
      } as any) as any;
      vacancyFiltersJustAppliedRef.current = {
        vacancyId: selectedVacancyId ?? null,
        filtersKey: getFiltersStateKey(nextFilters),
      };
      setFilters(nextFilters);
      setSearchMode('vacancy');
      
      // Update hash in storage
      if (selectedVacancyId) {
          localStorage.setItem(`vacancyHash_v${selectedVacancyId}`, currentHash);
      }
    }
  }, [selectedVacancyId, selectedVacancy, selectedVacancyLoading, getVacancyDataHash]);

  // Search results state
  const [searchData, setSearchData] = useState<SearchData | null>(null);
  const searchDataRef = useRef<SearchData | null>(null); // For "load more" without stale closure
  const searchDataScoringInputKeyRef = useRef<string | null>(null);
  const searchEvaluatedItemsRef = useRef<{ key: string | null; items: Record<string, any> }>({
    key: null,
    items: {},
  });
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false); // For "load more" button
  const [error, setError] = useState<any>(null);
  const [hhAuthRequired, setHhAuthRequired] = useState(false);
  const currentSearchIdRef = useRef<number>(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const lastAutoSearchFiltersRef = useRef<string | null>(null);
  const lastAutoSearchRelevanceRef = useRef<RelevanceFilter>(null);

  // Маппинг page → hhCursor для HH-постфильтров (курсор с которого начинать поиск для этой страницы)
  const jumperCursors = useRef<number[]>([0]);
  // Маппинг page → avitoCursor для Avito-постфильтров. Avito page начинается с 1.
  const avitoCursors = useRef<number[]>([1]);

  // Keep ref in sync with state
  useEffect(() => {
    searchDataRef.current = searchData;
  }, [searchData]);

  const getSearchScoringInputKey = useCallback((inputFilters?: any) => {
    const baseFilters = inputFilters || filters;
    const source = baseFilters?.source || filters.source || 'hh';
    const sanitized = sanitizeFiltersForSource(source, baseFilters as any) as any;
    const {
      page: _page,
      pageSize: _pageSize,
      limit: _limit,
      hhCursor: _hhCursor,
      avitoCursor: _avitoCursor,
      sessionId: _sessionId,
      ...scoringFilters
    } = sanitized;
    return JSON.stringify({
      vacancyId: selectedVacancyId ?? null,
      vacancyHash: getVacancyDataHash(),
      source,
      filters: scoringFilters,
      aiRequirementsWeighted: (selectedVacancy as any)?.aiRequirementsWeighted || null,
    });
  }, [filters, getVacancyDataHash, selectedVacancy, selectedVacancyId]);

  const resetSearchEvaluatedItems = useCallback((key: string | null = null) => {
    searchEvaluatedItemsRef.current = { key, items: {} };
  }, []);

  const handleSearchAiScoresChange = useCallback((items: any[]) => {
    if (!Array.isArray(items) || items.length === 0) return;
    const key = getSearchScoringInputKey();
    if (searchEvaluatedItemsRef.current.key !== key) {
      searchEvaluatedItemsRef.current = { key, items: {} };
    }
    for (const item of items) {
      if (item?.id == null) continue;
      searchEvaluatedItemsRef.current.items[String(item.id)] = item;
    }
  }, [getSearchScoringInputKey]);

  useEffect(() => {
    setIsCachedRelevantSearchResult(!!cachedResultsPayload?.isRelevantFiltered);
  }, [cachedResultsPayload?.isRelevantFiltered, selectedVacancyId]);
  const [viewMode, setViewMode] = useState<RelevanterViewMode>('search');
  const [isModalSearchActive, setIsModalSearchActive] = useState<boolean>(false);
  const [selectedRelevance, setSelectedRelevance] = useState<RelevanceFilter>(null);
  const [isCachedRelevantSearchResult, setIsCachedRelevantSearchResult] = useState<boolean>(
    !!cachedResultsPayload?.isRelevantFiltered
  );
  // TASKNEIROKLYUCH-645: страница поиска имеет ДВА независимых контрола количества:
  //   — RelevanceFilters «Количество кандидатов» (сверху) — target count для
  //     relevance-поиска. Живёт в локальном state relevancePageSize.
  //     handleSearch в relevance-ветке отправляет его как pageSize в бэк
  //     (перезаписывает значение из createApiQuery). В non-relevance режиме
  //     изменение НЕ трогает filters и не триггерит re-fetch.
  //   — SearchInterface «N резюме» (в searchParams-строке над таблицей) → filters.limit.
  //     Единый источник правды для client rowsPerPage И backend per_page —
  //     createApiQuery шлёт filters.limit как pageSize на бэк.
  // Таким образом смена одного не влияет на другое: верхний меняет target count
  // relevance-поиска, нижний — меняет размер страницы (и backend per_page).
  // filters.pageSize — legacy-поле, используется только для инициализации
  // relevancePageSize из кэша при загрузке страницы.
  const [relevancePageSize, setRelevancePageSize] = useState<number>((filters as any).pageSize || 20);
  // Визуальное отображение пресета: из активного выбора или из кеша (без влияния на логику поиска)
  const [cachedRelevancePreset, setCachedRelevancePreset] = useState<RelevanceFilter>(() => {
    try {
      const storage = selectedVacancyId ? localStorage : sessionStorage;
      const cacheKey = selectedVacancyId ? `searchResultsCache_v${selectedVacancyId}` : 'searchResultsCache';
      const raw = storage.getItem(cacheKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        return parsed.relevancePreset || null;
      }
    } catch {}
    return null;
  });
  const displayRelevance: RelevanceFilter = selectedRelevance || cachedRelevancePreset;
  // Tracks which selectedVacancyId the initial load effect ran for. `false` = never ran.
  // When the user switches vacancies, this no longer matches the current id and the effect re-runs.
  const initialLoadDoneRef = useRef<number | null | false>(false);
  const scrollRef = useScrollPosition('relevanter_list', selectedVacancyId as any) as React.MutableRefObject<HTMLDivElement | null>;

  const { getResponsesPageState, setResponsesPageState } = useVacancyStateCache();
  const cachedResponsesState = selectedVacancyId ? getResponsesPageState(selectedVacancyId) : null;
  const cachedResponsesMatchesSource = cachedResponsesState?.responsesSource === (filters.source || 'hh');

  const [searchTab, setSearchTabInternal] = useState<'search' | 'responses'>(cachedResponsesState?.searchTab || 'search');
  const [responsesData, setResponsesDataInternal] = useState<any[]>(cachedResponsesMatchesSource ? (cachedResponsesState?.responsesData || []) : []);
  const [responsesFound, setResponsesFoundInternal] = useState<number>(cachedResponsesMatchesSource ? (cachedResponsesState?.responsesFound || 0) : 0);
  const [responsesPerPage, setResponsesPerPageInternal] = useState<number>(cachedResponsesState?.responsesPerPage || 10);
  const [responsesLoadLimit, setResponsesLoadLimitInternal] = useState<number>(cachedResponsesState?.responsesLoadLimit || 100);
  const [responsesCurrentPage, setResponsesCurrentPageInternal] = useState<number>(cachedResponsesState?.responsesCurrentPage || 1);
  const [responsesLoading, setResponsesLoading] = useState(false);
  const [responsesRelevanceLoading, setResponsesRelevanceLoading] = useState(false);
  const [responsesRelevanceApplied, setResponsesRelevanceAppliedInternal] = useState<boolean>(cachedResponsesState?.responsesRelevanceApplied || false);
  const [responsesRelevancePreset, setResponsesRelevancePresetInternal] = useState<'high' | 'medium' | 'low' | null>(null);
  const [cachedResponsesRelevancePreset, setCachedResponsesRelevancePreset] = useState<'high' | 'medium' | 'low' | null>(cachedResponsesState?.responsesRelevancePreset || null);
  const displayResponsesRelevancePreset = responsesRelevancePreset || cachedResponsesRelevancePreset;
  const [responsesRelevanceTargetCount, setResponsesRelevanceTargetCount] = useState<number>(30);
  // aiEvaluations НЕ кэшируются на фронте: источник правды — БД бэка через
  // /evaluate-resume (там работает scoringCacheService с филтрхэшом
  // response_match). При перезагрузке страницы фронт-цикл в
  // VacancyResponsesInline сам подтянет оценки заново — кэш-hit'ы бесплатные.
  const [aiEvaluations, setAiEvaluationsInternal] = useState<Record<string, any>>({});
  const [evaluatingIds, setEvaluatingIds] = useState<Set<string>>(new Set());
  const [scoringCreditLimitHit, setScoringCreditLimitHit] = useState(false);
  // Аналог scoringStats из ResultsTable.js: показывает, сколько откликов оценено
  // за текущую сессию, сколько из кэша (бесплатно) и сколько новых (списано кр.).
  // Накапливается из двух источников: items find-relevant и поштучных
  // ответов /evaluate-resume — оба содержат fromCache.
  const [responsesScoringStats, setResponsesScoringStats] = useState<{ total: number; fromCache: number; newlyScored: number }>({ total: 0, fromCache: 0, newlyScored: 0 });
  const [hhVacancyNames, setHhVacancyNames] = useState<Record<string, string>>({});
  const [selectedResponses, setSelectedResponsesInternal] = useState<Set<string>>(new Set(cachedResponsesMatchesSource ? (cachedResponsesState?.selectedResponseIds || []) : []));

  const { progress: searchProgress, startTracking, stopTracking, generateSearchId, connect: connectWebSocket } = useSearchProgress();
  const [activeSearchId, setActiveSearchId] = useState<string | null>(null);
  const handleCancelActiveSearch = useCallback(() => {
    void cancelSearchSession(activeSearchId!).catch(() => {});
  }, [activeSearchId]);
  const setSearchTab = useCallback((tab: 'search' | 'responses') => {
    setSearchTabInternal(tab);
    if (tab !== 'search') {
      setSelectedRelevance(null);
    }
    if (tab !== 'responses') {
      setResponsesRelevancePresetInternal(null);
    }
    if (selectedVacancyId) {
      setResponsesPageState(selectedVacancyId, { searchTab: tab });
    }
  }, [selectedVacancyId, setResponsesPageState]);

  const handleSourceSwitch = useCallback((newSource: string) => {
    const currentSource = filters.source || 'hh';
    if (newSource === currentSource) return;

    currentSearchIdRef.current += 1;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    if (activeSearchId) {
      void cancelSearchSession(activeSearchId).catch(() => {});
    }
    stopTracking();
    setActiveSearchId(null);
    setLoading(false);
    setLoadingMore(false);
    setError(null);
    setHhAuthRequired(false);

    // Сохраняем уже очищенный state текущего source, чтобы чужие поля не
    // всплывали при возврате из Avito/HH/E-Staff.
    const currentFilters = sanitizeFiltersForSource(currentSource, filters as any);
    sourceFiltersRef.current[currentSource] = currentFilters;
    savePersistedFiltersForSource?.(currentFilters);

    // Восстанавливаем сохранённые фильтры нового источника или создаём
    // дефолтные, перенося только whitelist из source-policy.
    const saved = sourceFiltersRef.current[newSource] || getPersistedFiltersForSource?.(newSource);
    const nextFilters = buildFiltersForSourceSwitch({
      targetSource: newSource,
      currentFilters,
      savedTargetFilters: saved,
    }) as any;
    if (selectedVacancy && currentFilters.searchMode === 'vacancy') {
      const switchedFilters = applySourceVacancyFilters(newSource, nextFilters, {
        vacancy: selectedVacancy as any,
        vacancyId: selectedVacancyId as any,
        sourceFilters: currentFilters,
      }) as any;
      vacancyFiltersJustAppliedRef.current = {
        vacancyId: selectedVacancyId ?? null,
        filtersKey: getFiltersStateKey(switchedFilters),
      };
      setFilters(switchedFilters);
    } else {
      setFilters(nextFilters);
    }

    // Очищаем результаты при смене источника
    setSearchData(null);
    searchDataRef.current = null;

    // ID-пространства HH и E-Staff не пересекаются — выбранные кандидаты
    // и отклики из старого источника не имеют смысла для нового. Сбрасываем,
    // чтобы bulk-операции не оперировали чужими ID.
    setSelectedRelevance(null);
    setSelectedResponsesInternal(new Set());
    setResponsesDataInternal([]);
    setResponsesFoundInternal(0);
    setResponsesCurrentPageInternal(1);
    if (selectedVacancyId) {
      setResponsesPageState(selectedVacancyId, {
        responsesSource: newSource,
        responsesData: [],
        responsesFound: 0,
        responsesCurrentPage: 1,
        selectedResponseIds: [],
      });
    }

    if (newSource !== 'hh' && newSource !== 'avito' && searchTab === 'responses') {
      setSearchTab('search');
    }

    logInfo('Source switched', { from: currentSource, to: newSource });
  }, [
    activeSearchId,
    filters,
    getPersistedFiltersForSource,
    savePersistedFiltersForSource,
    selectedVacancy,
    selectedVacancyId,
    setFilters,
    searchTab,
    setSearchTab,
    setResponsesPageState,
    stopTracking,
  ]);

  useEffect(() => {
    if (!sourceNeedsNormalization || !resolvedSearchSource) return;
    handleSourceSwitch(resolvedSearchSource);
  }, [handleSourceSwitch, resolvedSearchSource, sourceNeedsNormalization]);

  useEffect(() => {
    if (searchTab !== 'responses') return;
    const source = (filters.source || 'hh').toString().toLowerCase();
    const vacancy = selectedVacancy as any;
    const hasResponsesSource =
      (source === 'hh' && (vacancy?.hhVacancyId || vacancy?.hhAdditionalVacancyIds?.length > 0))
      || (source === 'avito' && vacancy?.avitoVacancyId);
    if (!hasResponsesSource) {
      setSearchTab('search');
    }
  }, [filters.source, searchTab, selectedVacancy, setSearchTab]);

  useEffect(() => {
    if (vacancyFiltersJustAppliedRef.current) {
      return;
    }

    const sanitized = sanitizeFiltersForSource(filters.source || 'hh', filters as any);
    if (JSON.stringify(sanitized) !== JSON.stringify(filters)) {
      setFilters(sanitized as any);
    }
    // Очищаем только при инициализации/смене source: обычные updates идут через
    // handleFilterChange, чтобы не делать полный sanitize на каждый ввод.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.source, setFilters]);

  useEffect(() => {
    setSelectedRelevance(null);
    setResponsesRelevancePresetInternal(null);
    // TASKNEIROKLYUCH-645: relevancePageSize — локальный state, не зависит
    // от useSearchState. При смене вакансии его надо сбросить, иначе target
    // count из предыдущей вакансии «прилипнет» и применится при следующем
    // включении relevance-фильтра. Жёстко сбрасываем в дефолт 20: filters
    // здесь ещё СТАРЫЙ (хук useSearchState обновит его в отдельном эффекте
    // позже), поэтому читать filters.pageSize смысла нет.
    setRelevancePageSize(20);
    // Сбрасываем флаг начальной загрузки, чтобы initial load эффект проверил кеш заново
    initialLoadDoneRef.current = false;
    // Clear visible search results from the previous vacancy — otherwise the old
    // candidates stay on screen until the new fetch completes (or forever if the
    // cache-restore effect loads a different vacancy's cache).
    setSearchData(null);
    searchDataRef.current = null;
    // Обновляем кешированный пресет откликов при смене вакансии
    const newCachedResponsesState = selectedVacancyId ? getResponsesPageState(selectedVacancyId) : null;
    setCachedResponsesRelevancePreset(newCachedResponsesState?.responsesRelevancePreset || null);
    // Обновляем кешированный пресет релевантности при смене вакансии
    try {
      const storage = selectedVacancyId ? localStorage : sessionStorage;
      const cacheKey = selectedVacancyId ? `searchResultsCache_v${selectedVacancyId}` : 'searchResultsCache';
      const raw = storage.getItem(cacheKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        setCachedRelevancePreset(parsed.relevancePreset || null);
      } else {
        setCachedRelevancePreset(null);
      }
    } catch {
      setCachedRelevancePreset(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVacancyId]);

  const setResponsesData = useCallback((data: any[]) => {
    setResponsesDataInternal(data);
    if (selectedVacancyId) {
      setResponsesPageState(selectedVacancyId, { responsesData: data, responsesSource: filters.source || 'hh' });
    }
  }, [filters.source, selectedVacancyId, setResponsesPageState]);

  const setResponsesFound = useCallback((found: number) => {
    setResponsesFoundInternal(found);
    if (selectedVacancyId) {
      setResponsesPageState(selectedVacancyId, { responsesFound: found });
    }
  }, [selectedVacancyId, setResponsesPageState]);

  const setResponsesPerPage = useCallback((perPage: number) => {
    setResponsesPerPageInternal(perPage);
    if (selectedVacancyId) {
      setResponsesPageState(selectedVacancyId, { responsesPerPage: perPage });
    }
  }, [selectedVacancyId, setResponsesPageState]);

  const setResponsesLoadLimit = useCallback((limit: number) => {
    const nextLimit = Math.min(500, Math.max(1, Number.isFinite(limit) ? Math.floor(limit) : 100));
    setResponsesLoadLimitInternal(nextLimit);
    if (selectedVacancyId) {
      setResponsesPageState(selectedVacancyId, { responsesLoadLimit: nextLimit });
    }
  }, [selectedVacancyId, setResponsesPageState]);

  const setResponsesCurrentPage = useCallback((page: number) => {
    setResponsesCurrentPageInternal(page);
    if (selectedVacancyId) {
      setResponsesPageState(selectedVacancyId, { responsesCurrentPage: page });
    }
  }, [selectedVacancyId, setResponsesPageState]);

  const setResponsesRelevanceApplied = useCallback((applied: boolean) => {
    setResponsesRelevanceAppliedInternal(applied);
    if (selectedVacancyId) {
      setResponsesPageState(selectedVacancyId, { responsesRelevanceApplied: applied });
    }
  }, [selectedVacancyId, setResponsesPageState]);

  const setResponsesRelevancePreset = useCallback((preset: 'high' | 'medium' | 'low' | null) => {
    setResponsesRelevancePresetInternal(preset);
    setCachedResponsesRelevancePreset(preset);
    if (selectedVacancyId) {
      setResponsesPageState(selectedVacancyId, { responsesRelevancePreset: preset });
    }
  }, [selectedVacancyId, setResponsesPageState]);

  // aiEvaluations больше не персистятся в recruiterStateManager —
  // фронт-кэш убран, источник правды — БД бэка.
  const setAiEvaluations = setAiEvaluationsInternal;

  const setSelectedResponses = useCallback((selected: Set<string> | ((prev: Set<string>) => Set<string>)) => {
    setSelectedResponsesInternal(prev => {
      const newSelected = typeof selected === 'function' ? selected(prev) : selected;
      if (selectedVacancyId) {
        setResponsesPageState(selectedVacancyId, { selectedResponseIds: Array.from(newSelected) });
      }
      return newSelected;
    });
  }, [selectedVacancyId, setResponsesPageState]);

  const removeResponses = useCallback((responseIds: string[]) => {
    if (responseIds.length === 0) return;
    const idsToRemove = new Set(responseIds);
    setResponsesDataInternal(prev => {
      const filtered = prev.filter(r => !idsToRemove.has(r.id));
      if (selectedVacancyId) {
        setResponsesPageState(selectedVacancyId, { responsesData: filtered });
      }
      return filtered;
    });
    setResponsesFoundInternal(prev => {
      const newFound = Math.max(0, prev - responseIds.length);
      if (selectedVacancyId) {
        setResponsesPageState(selectedVacancyId, { responsesFound: newFound });
      }
      return newFound;
    });
    setSelectedResponsesInternal(prev => {
      const newSelected = new Set(prev);
      responseIds.forEach(id => newSelected.delete(id));
      if (selectedVacancyId) {
        setResponsesPageState(selectedVacancyId, { selectedResponseIds: Array.from(newSelected) });
      }
      return newSelected;
    });
  }, [selectedVacancyId, setResponsesPageState]);

  const [responsesParseMode, setResponsesParseModeInternal] = useState<'manual' | 'auto'>(cachedResponsesState?.parseMode || 'manual');

  const setResponsesParseMode = useCallback((mode: 'manual' | 'auto') => {
    setResponsesParseModeInternal(mode);
    if (selectedVacancyId) {
      setResponsesPageState(selectedVacancyId, { parseMode: mode });
    }
  }, [selectedVacancyId, setResponsesPageState]);

  const initialResponsesFilters = { showOnlyNew: true };

  const { 
    filters: responsesFilters, 
    updateFilters: updateResponsesFilters,
    resetFilters: resetResponsesFilters,
    getApiFilters: getResponsesApiFilters
  } = useResponsesFilters(
    initialResponsesFilters,
    selectedVacancyId,
    selectedVacancy
      ? `${selectedVacancy.id}-${vacancyFilters.totalExperience || ''}-${(vacancyFilters.cityIds || []).length}-${vacancyFilters.ageFrom || ''}-${vacancyFilters.ageTo || ''}-${vacancyFilters.positionName || ''}-${vacancyFilters.expectedPositionName || ''}-${vacancyFilters.salaryFrom || ''}-${vacancyFilters.salaryTo || ''}`
      : ''
  );

  const loadResponses = useCallback(async (
    forceReload = false,
    useFilters = true,
    pageOverride?: number,
    perPageOverride?: number
  ) => {
    if (!selectedVacancy) return;

    const vacancy = selectedVacancy as any;
    const responsesSource = (filters.source || 'hh').toString().toLowerCase();
    const effectivePage = pageOverride || responsesCurrentPage || 1;
    const effectivePerPage = perPageOverride || responsesPerPage;
    
    const hhVacancyIds: string[] = [];
    if (responsesSource === 'hh' && vacancy.hhVacancyId) {
      hhVacancyIds.push(vacancy.hhVacancyId);
    }
    if (responsesSource === 'hh' && vacancy.hhAdditionalVacancyIds && vacancy.hhAdditionalVacancyIds.length > 0) {
      hhVacancyIds.push(...vacancy.hhAdditionalVacancyIds);
    }
    const avitoVacancyId = responsesSource === 'avito' && vacancy.avitoVacancyId ? String(vacancy.avitoVacancyId) : '';

    if ((responsesSource !== 'hh' && responsesSource !== 'avito') || (hhVacancyIds.length === 0 && !avitoVacancyId)) {
      setResponsesData([]);
      setResponsesFound(0);
      setResponsesCurrentPage(1);
      return;
    }

    const cached = selectedVacancyId ? getResponsesPageState(selectedVacancyId) : null;
    if (!forceReload && cached?.responsesSource === responsesSource && cached?.responsesData && cached.responsesData.length > 0) {
      return;
    }

    setResponsesLoading(true);
    setResponsesRelevanceApplied(false);
    // Сбрасываем счётчик scoring stats на старте нового запроса откликов —
    // аналог clearAllAiState() в ResultsTable.js. Без этого при повторном
    // нажатии "Найти" цифры продолжают накапливаться с прошлой сессии.
    setResponsesScoringStats({ total: 0, fromCache: 0, newlyScored: 0 });
    try {
      const apiFilters: ResponsesFilters | undefined = useFilters && responsesSource === 'hh' ? getResponsesApiFilters() : undefined;
      const avitoApplicationState = responsesFilters.avitoApplicationState || DEFAULT_AVITO_APPLICATION_STATE;
      const avitoApplicationFilters: AvitoVacancyApplicationsFilters | undefined = useFilters && responsesSource === 'avito'
        ? {
          state: avitoApplicationState === ALL_AVITO_APPLICATION_STATES ? undefined : avitoApplicationState,
          isViewed: responsesFilters.avitoApplicationViewed === ''
            ? undefined
            : responsesFilters.avitoApplicationViewed === 'true',
          searchText: responsesFilters.searchText || undefined,
          ageFrom: responsesFilters.ageFrom || undefined,
          ageTo: responsesFilters.ageTo || undefined,
          salaryFrom: responsesFilters.salaryFrom || undefined,
          salaryTo: responsesFilters.salaryTo || undefined,
          gender: responsesFilters.gender || undefined,
          location: responsesFilters.areaIds,
          citizenship: responsesFilters.citizenship,
          educationLevel: responsesFilters.avitoEducationLevel,
          specialization: responsesFilters.avitoSpecialization,
          schedule: responsesFilters.avitoSchedule,
          businessTripReadiness: responsesFilters.avitoBusinessTrip,
          relocationReadiness: responsesFilters.avitoRelocation,
          experienceMin: responsesFilters.avitoExperienceMin || undefined,
          experienceMax: responsesFilters.avitoExperienceMax || undefined,
          driverLicence: responsesFilters.avitoDriverLicence || undefined,
          driverLicenceCategory: responsesFilters.avitoDriverLicenceCategory,
          desiredPosition: responsesFilters.desiredPosition || undefined,
          currentPosition: responsesFilters.currentPosition || undefined,
          orderBy: 'updated_at',
        }
        : undefined;
      logInfo('Loading responses with filters', { source: responsesSource, vacancyIds: hhVacancyIds, avitoVacancyId, filters: apiFilters, avitoApplicationFilters });

      const [hhData, avitoData] = await Promise.all([
        hhVacancyIds.length > 0
          ? getBatchVacancyResponsesPaged(hhVacancyIds, responsesPerPage, apiFilters, { noCache: forceReload })
          : Promise.resolve(null),
        avitoVacancyId
          ? getAvitoVacancyApplications(avitoVacancyId, avitoApplicationFilters, effectivePage, effectivePerPage)
          : Promise.resolve(null),
      ]);
      let items: any[] = [];
      if (Array.isArray(hhData?.items)) {
        items = hhData.items;
      } else if (Array.isArray(hhData?.itemsByPage)) {
        items = hhData.itemsByPage.flatMap((p: any[]) => Array.isArray(p) ? p : []);
      }
      const avitoItems = Array.isArray(avitoData?.items) ? avitoData.items : [];
      items = [...items, ...avitoItems];
      const hhFoundFromApi = typeof hhData?.found === 'number' ? hhData.found : null;
      const avitoFoundFromApi = typeof avitoData?.count === 'number' ? avitoData.count : avitoItems.length;
      const found = (hhFoundFromApi != null ? hhFoundFromApi : items.length - avitoItems.length) + avitoFoundFromApi;

      setResponsesFound(found);
      setResponsesCurrentPage(responsesSource === 'avito' ? effectivePage : 1);
      setResponsesData(items);

      if (hhData?.partial) {
        toast.error('HH.ru временно отвечает нестабильно: показаны не все отклики. Нажмите «Найти» ещё раз.');
      }

      logInfo('Responses loaded', { source: responsesSource, found, perPage: responsesPerPage, vacancyIds: hhVacancyIds, avitoVacancyId });
    } catch (err) {
      logError('Error loading responses', err);
      toast.error('Не удалось загрузить отклики. Попробуйте ещё раз.');
    } finally {
      setResponsesLoading(false);
    }
  }, [filters.source, selectedVacancy, selectedVacancyId, getResponsesPageState, setResponsesData, setResponsesFound, setResponsesCurrentPage, getResponsesApiFilters, responsesFilters, responsesCurrentPage, responsesPerPage]);

  const findRelevantResponses = useCallback(async (params: { targetCount: number; minRating: number | null; maxRating?: number | null }) => {
    if (!selectedVacancy) return;

    const vacancy = selectedVacancy as any;
    const hhVacancyIds: string[] = [];
    if (vacancy.hhVacancyId) {
      hhVacancyIds.push(vacancy.hhVacancyId);
    }
    if (vacancy.hhAdditionalVacancyIds && vacancy.hhAdditionalVacancyIds.length > 0) {
      hhVacancyIds.push(...vacancy.hhAdditionalVacancyIds);
    }

    if (hhVacancyIds.length === 0) {
      setResponsesData([]);
      setResponsesFound(0);
      setResponsesCurrentPage(1);
      return;
    }

    setResponsesRelevanceLoading(true);
    setResponsesLoading(true);
    // Сбрасываем счётчик scoring stats на старте нового поиска,
    // чтобы цифры относились к текущей сессии find-relevant, а не к предыдущей.
    setResponsesScoringStats({ total: 0, fromCache: 0, newlyScored: 0 });

    // searchId + WS-трекинг: для прогресса и кнопки «Стоп»
    const searchId = generateSearchId();
    connectWebSocket();
    startTracking(searchId);
    setActiveSearchId(searchId);

    try {
      const apiFilters: ResponsesFilters | undefined = getResponsesApiFilters();
      const data = await findRelevantVacancyResponses({
        vacancyIds: hhVacancyIds,
        targetCount: params.targetCount,
        minRating: params.minRating,
        maxRating: params.maxRating ?? null,
        filters: apiFilters,
        orderBy: responsesFilters?.orderBy,
        baseVacancy: selectedVacancy,
        scoringEnabled: vacancyScoringEnabled,
        searchId
      });

      // Проверяем ответ на исчерпание лимитов
      if (data?.scoringUnavailable && data?.reason === 'CREDIT_LIMIT') {
        setScoringCreditLimitHit(true);
      }

      const items: any[] = Array.isArray(data?.items) ? data.items : [];
      const found = typeof data?.matched === 'number' ? data.matched : items.length;
      setResponsesFound(found);
      setResponsesCurrentPage(1);
      setResponsesData(items);
      setResponsesRelevanceApplied(true);

      // Записываем оценки из find-relevant в aiEvaluations, чтобы UI не запускал повторную оценку
      const evaluationsFromRelevance: Record<string, any> = {};
      for (const item of items) {
        const vacId = item.sourceVacancyId || item.vacancy?.id;
        if (vacId && item.id && item.aiEvaluation) {
          evaluationsFromRelevance[`${vacId}_${item.id}`] = item.aiEvaluation;
        }
      }
      if (Object.keys(evaluationsFromRelevance).length > 0) {
        setAiEvaluations(prev => ({ ...prev, ...evaluationsFromRelevance }));
      }

      // Перезаписываем scoring stats результатом find-relevant — точно как в
      // ResultsTable.js (строка 363) при scoringApplied=true: stats относятся
      // к ПОСЛЕДНЕМУ запросу, не аккумулируются между нажатиями «Найти».
      // Поштучные дооценки через evaluateResponseResume накапливаются поверх.
      const scoredItems = items.filter((i) => i.aiEvaluation);
      const cached = scoredItems.filter((i) => i.fromCache).length;
      setResponsesScoringStats({
        total: scoredItems.length,
        fromCache: cached,
        newlyScored: scoredItems.length - cached
      });
    } catch (err) {
      logError('Error finding relevant responses', err);
      setResponsesData([]);
      setResponsesFound(0);
      setResponsesCurrentPage(1);
      setResponsesRelevanceApplied(false);
    } finally {
      setResponsesRelevanceLoading(false);
      setResponsesLoading(false);
      stopTracking();
      setActiveSearchId(null);
    }
    // vacancyScoringEnabled из деклараций ниже — намеренно не в deps (TDZ);
    // closure корректно подхватит актуальное значение через capture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVacancy, setResponsesData, setResponsesFound, setResponsesCurrentPage, getResponsesApiFilters, responsesFilters.orderBy, setAiEvaluations, generateSearchId, connectWebSocket, startTracking, stopTracking]);

  const clearRelevantResponses = useCallback(() => {
    setResponsesRelevanceApplied(false);
    // Скрываем счётчик scoring stats при возврате к обычному списку откликов:
    // он показывает результаты последнего find-relevant, без релевантности он
    // ни к чему не относится.
    setResponsesScoringStats({ total: 0, fromCache: 0, newlyScored: 0 });
    void loadResponses(true, true);
  }, [loadResponses, setResponsesRelevanceApplied]);

  const handleResponsesSearch = useCallback(async () => {
    await loadResponses(true, true, 1);
    if ((filters.source || 'hh') !== 'hh') return;
    if (!responsesRelevancePreset) return;

    const preset = responsesRelevancePreset;
    const targetCount = responsesRelevanceTargetCount;
    if (!Number.isFinite(targetCount) || targetCount <= 0) return;

    if (preset === 'high') {
      await findRelevantResponses({ targetCount, minRating: 85 });
      return;
    }
    if (preset === 'medium') {
      await findRelevantResponses({ targetCount, minRating: 70, maxRating: 84 });
      return;
    }
    await findRelevantResponses({ targetCount, minRating: 0, maxRating: 69 });
  }, [filters.source, findRelevantResponses, loadResponses, responsesRelevancePreset, responsesRelevanceTargetCount]);

  const handleResponsesSearchRef = useRef(handleResponsesSearch);
  handleResponsesSearchRef.current = handleResponsesSearch;

  const handleClearResponsesFilters = useCallback(() => {
    resetResponsesFilters();
    setResponsesRelevancePreset(null);
    setResponsesRelevanceApplied(false);
    setTimeout(() => {
      handleResponsesSearchRef.current();
    }, 50);
  }, [resetResponsesFilters, setResponsesRelevanceApplied]);

  useEffect(() => {
    if (!responsesRelevanceApplied) return;
    if (!Array.isArray(responsesData) || responsesData.length === 0) return;
  }, [responsesFilters.orderBy, responsesData, responsesRelevanceApplied, setResponsesData]);

  useEffect(() => {
    if (searchTab === 'responses' && selectedVacancy) {
      loadResponses();
    }
  }, [searchTab, selectedVacancy, loadResponses]);

  useEffect(() => {
    if (viewMode !== 'search') return;
    if (responsesRelevancePreset && searchTab === 'responses') {
      void handleResponsesSearch();
    }
  }, [responsesRelevancePreset, searchTab, handleResponsesSearch, viewMode]);

  const vacancyScoringEnabled = (selectedVacancy as any)?.scoringEnabled !== false;

  // Сброс лимита при смене вакансии
  const prevVacancyIdRef = React.useRef(selectedVacancyId);
  React.useEffect(() => {
    if (prevVacancyIdRef.current !== selectedVacancyId) {
      setScoringCreditLimitHit(false);
      // Сбрасываем счётчик scoring stats при переключении вакансии —
      // цифры должны относиться только к текущей открытой вакансии.
      setResponsesScoringStats({ total: 0, fromCache: 0, newlyScored: 0 });
      // Сбрасываем aiEvaluations: фронт-кэш убран, оценки подтянутся
      // из БД заново для новой вакансии через фронт-цикл /evaluate-resume.
      setAiEvaluations({});
      prevVacancyIdRef.current = selectedVacancyId;
    }
  }, [selectedVacancyId]);

  const evaluateResponseResume = useCallback(async (vacancyId: string, responseId: string, resumeData: any) => {
    // Если скоринг отключён на уровне вакансии — не оцениваем
    if (!vacancyScoringEnabled) return;
    // Если лимит уже исчерпан — не отправляем новые запросы
    if (scoringCreditLimitHit) return;

    const evaluationKey = `${vacancyId}_${responseId}`;

    if (evaluatingIds.has(evaluationKey)) {
      return;
    }

    const existing = aiEvaluations[evaluationKey];
    if (existing && existing?.comment !== 'Ошибка оценки') {
      return;
    }

    setEvaluatingIds(prev => new Set([...Array.from(prev), evaluationKey]));

    try {
      const response = await evaluateResume(vacancyId, resumeData, selectedVacancy, vacancyScoringEnabled);

      // Проверяем ответ на исчерпание лимитов
      if (response?.scoringUnavailable && response?.reason === 'CREDIT_LIMIT') {
        setScoringCreditLimitHit(true);
        return;
      }

      // Не учитываем в stats вырожденные ответы (например, scoringUnavailable
      // с SCORING_DISABLED) — там нет реальной оценки.
      if (!response?.scoringUnavailable) {
        const wasCached = !!response?.fromCache;
        setResponsesScoringStats(prev => ({
          total: prev.total + 1,
          fromCache: prev.fromCache + (wasCached ? 1 : 0),
          newlyScored: prev.newlyScored + (wasCached ? 0 : 1)
        }));
      }

      setAiEvaluations(prev => ({
        ...prev,
        [evaluationKey]: response
      }));
    } catch (error) {
      console.error('Error evaluating resume:', error);
      setAiEvaluations(prev => ({
        ...prev,
        [evaluationKey]: { rating: 0, comment: 'Ошибка оценки' }
      }));
    } finally {
      setEvaluatingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(evaluationKey);
        return newSet;
      });
    }
  }, [evaluatingIds, aiEvaluations, selectedVacancy, vacancyScoringEnabled, scoringCreditLimitHit]);

  useEffect(() => {
    const loadHhVacancyNames = async () => {
      if (!selectedVacancy) return;

      const vacancy = selectedVacancy as any;
      const vacancyIds: string[] = [];
      if (vacancy.hhVacancyId) {
        vacancyIds.push(vacancy.hhVacancyId);
      }
      if (vacancy.hhAdditionalVacancyIds) {
        vacancyIds.push(...vacancy.hhAdditionalVacancyIds);
      }

      const names: Record<string, string> = {};
      for (const id of vacancyIds) {
        try {
          const response = await getHHVacancyById(id);
          names[id] = response?.vacancy?.name || `Вакансия ${id}`;
        } catch {
          names[id] = `Вакансия ${id}`;
        }
      }
      setHhVacancyNames(names);
    };

    const vacancy = selectedVacancy as any;
    if (selectedVacancy && (vacancy.hhVacancyId || vacancy.hhAdditionalVacancyIds?.length > 0)) {
      loadHhVacancyNames();
    }
  }, [selectedVacancy]);

  useEffect(() => {
    const syncCityIdsFromCities = async () => {
      if ((filters.source || 'hh') !== 'hh') {
        return;
      }
      if (
        Array.isArray(filters.cities) &&
        filters.cities.length > 0 &&
        (!filters.cityIds || filters.cityIds.length === 0)
      ) {
        try {
          const ids: string[] = [];
          for (const cityName of filters.cities) {
            const id = await getAreaIdByName(String(cityName));
            if (id && !ids.includes(id)) {
              ids.push(id);
            }
          }
          if (ids.length > 0) {
            setFilters({ ...(filters as any), cityIds: ids } as any);
          }
        } catch (e) {
          logError('Failed to map cities to cityIds', e);
        }
      }
    };
    void syncCityIdsFromCities();
  }, [filters.cities, filters.cityIds, setFilters]);

  // Обертка над setViewMode, чтобы запрещать admin-режимы
  const safeSetViewMode = useCallback((mode: RelevanterViewMode) => {
    if (!canAccessAdminViews && (mode === 'admin-panel' || mode === 'statistics' || mode === 'activity' || mode === 'hh-settings')) {
      // Принудительно возвращаем в поиск, если нет прав
      setViewMode('search');
      // Сбрасываем admin-параметры в URL, если они есть
      const params = new URLSearchParams(location.search);
      if (params.get('admin')) {
        params.delete('admin');
        params.delete('tab');
        navigate({ pathname: '/recruiter/search', search: params.toString() ? `?${params.toString()}` : '' }, { replace: true });
      }
      return;
    }
    setViewMode(mode);
  }, [canAccessAdminViews, location.search, navigate]);

  // Sync auth to Relevanter (preview bridge)
  const syncAuthToRelevanter = useCallback(() => {
    if (token && user) {
      localStorage.setItem('relevanter_preview', 'true');
      localStorage.setItem('adminToken', token);
      localStorage.setItem('adminUser', JSON.stringify(user));
      localStorage.setItem('userRoles', JSON.stringify(['admin']));
      localStorage.setItem('username', user.email || 'admin');
      logInfo('Auth synced to Relevanter', { user: user.email });
    }
  }, [token, user]);

  const handleSearchValueChange = (value: string) => {
    handleFilterChange({ positionName: value });
  };

  // Ensure auth is synced as soon as token becomes available
  useEffect(() => {
    if (token) {
      syncAuthToRelevanter();
    }
  }, [token, syncAuthToRelevanter]);

  // Handle search
  const handleSearch = useCallback(async (options?: { resetJumperCursors?: boolean }) => {
    // При старте поиска показываем область результатов, не скрывая фильтры
    setViewMode('search');

    if (
      selectedVacancyId &&
      (selectedVacancyLoading || !selectedVacancy || selectedVacancy.id !== selectedVacancyId)
    ) {
      logInfo('Search postponed until selected vacancy is loaded', { selectedVacancyId });
      return;
    }

    if (!searchSourcesLoaded || sourceNeedsNormalization) {
      logInfo('Search postponed until source availability is resolved', {
        source: filters.source,
        resolvedSource: resolvedSearchSource,
      });
      return;
    }

    if (hasNoAvailableSearchSources) {
      currentSearchIdRef.current += 1;
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
      setLoading(false);
      setLoadingMore(false);
      setSearchData(null);
      searchDataRef.current = null;
      setHhAuthRequired(false);
      setError(new Error('Нет доступных источников поиска. Подключите хотя бы один источник кандидатов в настройках.'));
      return;
    }

    if (options?.resetJumperCursors) {
      jumperCursors.current = [0];
      avitoCursors.current = [1];
      // Сбрасываем страницу на 1 если мы не на первой
      if (
        (filters as any).page > 1
        && (
          (filters as any).excludeJumpers
          || (filters as any).filterByExpIndustry
          || (filters as any).currentPositionOnly
          || ((filters as any).source === 'avito' && (filters as any).strictSearch)
        )
      ) {
        setFilters({ ...(filters as any), page: 1 } as any);
      }
    }

    // DISABLED: HH token check - let user decide when to authorize
    // if (filters.source === 'hh' && searchMode !== 'vacancy') {
    //   try {
    //     const hhTokenData = await getHHToken();
    //     if (!hhTokenData?.tokenMasked) {
    //       logError('Attempted HH search without HH token');
    //       setViewMode('hh-auth');
    //       return;
    //     }
    //     logInfo('HH token found, proceeding with search');
    //   } catch (error) {
    //     logError('Error checking HH token:', error);
    //     setViewMode('hh-auth');
    //     return;
    //   }
    // }

    const myId = Date.now();
    currentSearchIdRef.current = myId;

    // Отменяем предыдущий запрос, если он ещё выполняется
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Check if this is a "load more" request
    const requestFilters = sanitizeFiltersForSource(filters.source || 'hh', filters as any);
    const params = createApiQuery(requestFilters as any);
    const searchScoringInputKey = getSearchScoringInputKey(requestFilters as any);

    // Attach weighted requirements from the selected vacancy so relevanter-back scoring
    // can evaluate each candidate against the exact required/optional list saved on the position.
    // Without this, scoring falls back to extracting requirements from the vacancy description,
    // which often drifts from what the recruiter actually configured.
    const selectedVacancyAny = selectedVacancy as any;
    if (selectedVacancyAny?.aiRequirementsWeighted) {
      (params as any).aiRequirementsWeighted = selectedVacancyAny.aiRequirementsWeighted;
    }

    const currentData = searchDataRef.current;
    const isLoadMoreRequest = selectedRelevance && requestFilters.source === 'hh' && params.page > 1 && (currentData?.items?.length ?? 0) > 0;
    if (!selectedRelevance && !isLoadMoreRequest) {
      resetSearchEvaluatedItems(searchScoringInputKey);
    }

    // Пост-фильтры (джамперы / отрасль / только текущая должность): определяем hhCursor из маппинга страниц
    const hasPostFilter = needsPostFilter(params as any);
    if (hasPostFilter) {
      if (options?.resetJumperCursors && params.page > 1) {
        params.page = 1;
      }
      const pageNum = params.page || 1;
      const cursor = jumperCursors.current[pageNum - 1] ?? 0;
      (params as any).hhCursor = cursor;
    }
    const hasAvitoStrictPostFilter = requestFilters.source === 'avito'
      && (params as any).strictSearch === true
      && typeof (params as any).positionName === 'string'
      && (params as any).positionName.trim() !== '';
    if (hasAvitoStrictPostFilter) {
      if (options?.resetJumperCursors && params.page > 1) {
        params.page = 1;
      }
      const pageNum = params.page || 1;
      const cursor = avitoCursors.current[pageNum - 1] ?? pageNum;
      (params as any).avitoCursor = cursor;
    }

    // Use loadingMore for "load more", regular loading otherwise
    if (isLoadMoreRequest) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);
    setHhAuthRequired(false);

    try {
      // Санитизируем параметры перед запросом: исключаем пустые строки/пустые поля
      // Доп. фильтрация: если выбран родитель, не отправляем его дочерние ID
      if (requestFilters.source === 'hh' && Array.isArray((params as any).cityIds) && (params as any).cityIds.length > 0) {
        try {
          (params as any).cityIds = await compressCityIds((params as any).cityIds);
        } catch (e) {
          console.warn('[RELEVANTER] compressCityIds failed, sending original cityIds');
        }
      }
      
      // Разворачиваем родительские специализации на дочерние ID только для запроса к бэкенду
      const selectedSpecializations = (requestFilters as any).selectedSpecializations || [];
      if (requestFilters.source === 'hh' && Array.isArray(selectedSpecializations) && selectedSpecializations.length > 0) {
        try {
          const expandedIds = await expandSpecializationIds(selectedSpecializations);
          const processedIds = Array.from(
            new Set(
              expandedIds
                .map((v: any) => extractLastPathSegment(String(v)))
                .filter(Boolean)
                .filter((id: string) => String(id).trim() !== '')
            )
          );
          if (processedIds.length > 0) {
            (params as any).professional_roles = processedIds;
          } else {
            delete (params as any).professional_roles;
          }
        } catch (e) {
          console.warn('[RELEVANTER] expandSpecializationIds failed, using original professional_roles', e);
        }
      }
      
      // Запоминаем фильтры и релевантность, по которым был выполнен последний поиск (для авто-поиска).
      // TASKNEIROKLYUCH-645: filters.limit теперь идёт в бэк как per_page
      // (см. createApiQuery), поэтому его смена ДОЛЖНА инвалидировать снапшот.
      try {
        lastAutoSearchFiltersRef.current = getFiltersStateKey(requestFilters as any);
        lastAutoSearchRelevanceRef.current = selectedRelevance;
      } catch {
        lastAutoSearchFiltersRef.current = null;
      }

      logInfo('Starting search with params', params);

      let result;
      if (selectedRelevance) {
        // Не очищаем searchData — показываем старые данные пока грузятся новые

        // Generate unique search ID for WebSocket progress tracking
        const searchId = generateSearchId();

        // Connect WebSocket and start tracking
        connectWebSocket();
        startTracking(searchId);
        setActiveSearchId(searchId);

        // TASKNEIROKLYUCH-645: target count для AI-поиска — relevancePageSize
        // (локальный state, не путать с filters.pageSize). Перезаписываем
        // pageSize из createApiQuery (которое равно filters.limit для обычного
        // поиска) на relevancePageSize — бэк auto-fetch до этого количества
        // релевантных кандидатов.
        const searchParams: any = {
          ...params,
          relevanceFilter: selectedRelevance,
          pageSize: relevancePageSize,
          searchId,
        };
        const evaluatedItemsById = new Map<string, any>();
        const currentItems = searchDataScoringInputKeyRef.current === searchScoringInputKey
          ? currentData?.items
          : [];
        const evaluatedCurrentItems = Array.isArray(currentItems) ? currentItems : [];
        evaluatedCurrentItems.forEach((item: any) => {
          if (item?.id != null && (typeof item?.aiRating === 'number' || item?.aiComment)) {
            evaluatedItemsById.set(String(item.id), item);
          }
        });
        if (searchEvaluatedItemsRef.current.key === searchScoringInputKey) {
          Object.values(searchEvaluatedItemsRef.current.items).forEach((item: any) => {
            if (item?.id != null) evaluatedItemsById.set(String(item.id), item);
          });
        } else {
          resetSearchEvaluatedItems(searchScoringInputKey);
        }
        const evaluatedItems = Array.from(evaluatedItemsById.values());
        if (!isLoadMoreRequest && evaluatedItems.length > 0) {
          searchParams.evaluatedItems = evaluatedItems;
        }
        // Передаём hhCursor для дозагрузки (load more) при фильтре джамперов
        if (isLoadMoreRequest && currentData?.hhCursor !== undefined) {
          searchParams.hhCursor = currentData.hhCursor;
        }
        logInfo('Using search-with-relevance endpoint', {
          relevanceFilter: selectedRelevance,
          searchParams: Object.keys(searchParams),
          hasRelevanceFilter: 'relevanceFilter' in searchParams,
          isLoadMore: isLoadMoreRequest,
          searchId
        });
        // Выбираем endpoint в зависимости от источника
        if (requestFilters.source === 'hh' || !requestFilters.source) {
          result = await searchHHWithRelevance(searchParams, controller.signal);
        } else if (requestFilters.source === 'avito') {
          // Avito: пока MVP, без AI relevance — простой GET /api/avito/search.
          // searchAvitoResumes сама достанет location из cityIds, page/per_page
          // из searchParams. Прочие фильтры подключим следующими задачами.
          result = await searchAvitoResumes(searchParams, controller.signal);
        } else {
          searchParams.source = requestFilters.source;
          result = await searchEStaffWithRelevance(searchParams, controller.signal);
        }

        // Stop tracking after search completes
        stopTracking();
        setActiveSearchId(null);
        logInfo('Relevance search completed', {
          itemCount: result?.items?.length,
          scoringApplied: result?.scoringApplied,
          relevanceFilterUsed: result?.relevanceFilter,
          cancelled: result?.cancelled === true
        });
      } else if (requestFilters.source === 'avito') {
        // Avito без AI-relevance: тот же searchAvitoResumes, что и в
        // ветке выше — у нас пока нет /api/avito/search-with-relevance,
        // поэтому маршрут одинаковый для обоих режимов. params содержит
        // cityIds/page/limit, проброс location идёт внутри searchAvitoResumes.
        result = await searchAvitoResumes(params, controller.signal);
      } else {
        result = await searchApplicants(params, controller.signal);
      }

      if (currentSearchIdRef.current !== myId) {
        return;
      }

      // For "load more" with relevance filter, append results instead of replacing
      if (isLoadMoreRequest && result?.items) {
        const existingItems = currentData?.items || [];
        const newItems = result.items.filter((item: any) =>
          !existingItems.some((existing: any) => existing.id === item.id)
        );
        const mergedItems = [...existingItems, ...newItems];
        const estimatedFilteredTotal = Math.max(
          Number(result.estimatedFilteredTotal ?? result.count ?? 0),
          mergedItems.length
        );
        result = {
          ...result,
          items: mergedItems,
          count: estimatedFilteredTotal,
          itemsCount: mergedItems.length,
          estimatedFilteredTotal,
          filteredCount: mergedItems.length
        };
      }

      setSearchData(result);
      searchDataScoringInputKeyRef.current = searchScoringInputKey;

      // Проверяем исчерпание лимитов скоринга в результатах поиска
      if (result?.hardStop && result?.reason === 'CREDIT_LIMIT') {
        setScoringCreditLimitHit(true);
      }
      if (result?.scoringUnavailable && result?.reason === 'CREDIT_LIMIT') {
        setScoringCreditLimitHit(true);
      }

      // Сохраняем hhCursor для следующей страницы (для любого пост-фильтра)
      if (hasPostFilter && result?.hhCursor !== undefined) {
        const pageNum = params.page || 1;
        jumperCursors.current[pageNum] = result.hhCursor;
      }
      if (hasAvitoStrictPostFilter && result?.avitoCursor !== undefined) {
        const pageNum = params.page || 1;
        avitoCursors.current[pageNum] = result.avitoCursor;
      }

      setIsCachedRelevantSearchResult(!!selectedRelevance);
      setCachedRelevancePreset(selectedRelevance);
      try {
        cacheResults?.(result, {
          isRelevantFiltered: !!selectedRelevance,
          relevancePreset: selectedRelevance,
          jumperCursors: jumperCursors.current,
          avitoCursors: avitoCursors.current,
        });
      } catch {}
      logInfo('Search completed successfully', { count: result?.count });
    } catch (err: any) {
      // Отменённый запрос — не ошибка, просто игнорируем
      if (axios.isCancel(err) || err?.code === 'ERR_CANCELED') {
        logInfo('Search aborted (new search started)');
        return;
      }
      if (currentSearchIdRef.current !== myId) return;
      setError(err);
      const message = String(err?.response?.data?.error?.message || err?.message || '');
      const status = err?.response?.status;
      const looksLikeAuth =
        requestFilters.source === 'hh' &&
        (status === 401 || status === 403 || /авториз/i.test(message));
      if (looksLikeAuth) {
        setHhAuthRequired(true);
      }
      logError('Search failed', err);
    } finally {
      if (currentSearchIdRef.current === myId) {
        setLoading(false);
        setLoadingMore(false);
      }
      if (selectedRelevance) {
        setActiveSearchId(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, token, searchMode, selectedRelevance, relevancePageSize, selectedVacancy, selectedVacancyId, selectedVacancyLoading, generateSearchId, connectWebSocket, startTracking, stopTracking, getSearchScoringInputKey, resetSearchEvaluatedItems, hasNoAvailableSearchSources, resolvedSearchSource, searchSourcesLoaded, sourceNeedsNormalization]); // searchData intentionally excluded to avoid infinite loops

  // Store handleSearch in ref to avoid stale closures (moved here so it's available for handleRelevanceChange)
  const handleSearchRef = useRef(handleSearch);
  handleSearchRef.current = handleSearch;

  // Handle filter changes
  // TASKNEIROKLYUCH-645: filters.limit — единый источник правды для client
  // rowsPerPage И backend per_page (см. createApiQuery). filters.pageSize —
  // отдельный параметр target count для relevance-поиска, его здесь не
  // трогаем, чтобы смена «N резюме» не затирала выбор «Количество кандидатов».
  const handleFilterChange = useCallback((newFilters: Partial<FiltersState>) => {
    jumperCursors.current = [0];
    avitoCursors.current = [1];
    resetSearchEvaluatedItems();
    searchDataScoringInputKeyRef.current = null;
    const targetSource = (newFilters.source || filters.source || 'hh') as string;
    setFilters(sanitizeFiltersForSource(targetSource, {
      ...(filters as any),
      ...newFilters,
      source: targetSource,
      page: 1,
    }) as any);
    logInfo('Filters updated', newFilters);
  }, [filters, resetSearchEvaluatedItems, setFilters]);

  // Handle page change (только для обычного режима — в relevance-режиме
  // ResultsTable использует локальный clientPage и сюда не дёргает).
  const handlePageChange = useCallback((newPage: number, pageSize?: number) => {
    const next = { ...(filters as any), page: newPage + 1, pageSize: pageSize || (filters as any).pageSize } as any;
    setFilters(next);
  }, [filters, setFilters]);

  // Handle relevance filter change - reset page and clear results
  const handleRelevanceChange = useCallback((newRelevance: RelevanceFilter) => {
    jumperCursors.current = [0];
    avitoCursors.current = [1];
    setIsCachedRelevantSearchResult(false);
    setSearchData(null);
    searchDataRef.current = null;
    searchDataScoringInputKeyRef.current = null;
    setFilters({ ...(filters as any), page: 1 } as any);
    setSelectedRelevance(newRelevance);
    logInfo('Relevance filter changed', { newRelevance });

    // Trigger search after state updates
    // Small delay to ensure React has processed state updates
    setTimeout(() => {
      handleSearchRef.current({ resetJumperCursors: true });
    }, 50);
  }, [filters, setFilters]);

  // Handle relevance target count change.
  // TASKNEIROKLYUCH-645: «Количество кандидатов» — это target count ТОЛЬКО для
  // relevance-поиска. В non-relevance режиме оно не должно влиять ни на бэк,
  // ни на клиентскую пагинацию — иначе пользователь видит, что верхний
  // контрол меняет число строк на странице. В non-relevance режиме просто
  // сохраняем в локальный стейт; применится, когда будет включён фильтр
  // релевантности (handleSearch читает relevancePageSize напрямую).
  const handleRelevancePageSizeChange = useCallback((newSize: number) => {
    setRelevancePageSize(newSize);

    if (!selectedRelevance) {
      logInfo('Relevance target count preference saved (non-relevance mode)', { newSize });
      return;
    }

    setSearchData(null);
    searchDataRef.current = null;
    searchDataScoringInputKeyRef.current = null;
    jumperCursors.current = [0];
    avitoCursors.current = [1];
    setFilters({ ...(filters as any), page: 1, pageSize: newSize } as any);
    logInfo('Relevance target count changed', { newSize });

    if (filters.source === 'hh') {
      setTimeout(() => {
        handleSearchRef.current({ resetJumperCursors: true });
      }, 50);
    }
  }, [filters, setFilters, selectedRelevance]);

  // Debounced auto-search on filter change
  useEffect(() => {
    // Не запускаем автопоиск, если открыта модалка фильтров
    if (isModalSearchActive) {
      return;
    }

    if (
      selectedVacancyId &&
      (selectedVacancyLoading || !selectedVacancy || selectedVacancy.id !== selectedVacancyId)
    ) {
      return;
    }

    if (!searchSourcesLoaded || sourceNeedsNormalization || hasNoAvailableSearchSources) {
      return;
    }

    // Don't run on initial load, that's handled by another effect
    if (initialLoadDoneRef.current === false) {
      return;
    }

    // Build current search key (filters + relevance).
    // TASKNEIROKLYUCH-645: filters.limit идёт в бэк как per_page, поэтому его
    // смена должна триггерить новый запрос (иначе при смене «N резюме» бэк
    // не перезапрашивается и данные остаются прежними).
    let currentFiltersKey: string | null = null;
    try {
      currentFiltersKey = getFiltersStateKey(filters as any);
    } catch {
      currentFiltersKey = null;
    }

    // Check if search key AND relevance are the same as last search
    const filtersUnchanged = currentFiltersKey && lastAutoSearchFiltersRef.current === currentFiltersKey;
    const relevanceUnchanged = lastAutoSearchRelevanceRef.current === selectedRelevance;

    if (filtersUnchanged && relevanceUnchanged) {
      return;
    }

    // If only relevance changed, let the relevance-specific effect handle it
    if (filtersUnchanged && !relevanceUnchanged) {
      return;
    }

    // Debounce the search call
    const handler = setTimeout(() => {
      handleSearch();
    }, 500); // 500ms delay

    // Cleanup function to cancel the timeout if filters change again
    return () => {
      clearTimeout(handler);
    };
  }, [filters, handleSearch, hasNoAvailableSearchSources, isModalSearchActive, searchSourcesLoaded, selectedRelevance, selectedVacancy, selectedVacancyId, selectedVacancyLoading, sourceNeedsNormalization]);

  // При монтировании/возврате в поиск: сначала пробуем синхронно восстановить кэш (без гонок), иначе авто-запрос.
  // Track which vacancyId the initial load ran for. When the user switches vacancies, the ref no longer matches
  // the current selectedVacancyId and we re-run the cache-restore / auto-search logic for the NEW vacancy
  // (otherwise the previous vacancy's results stay visible).
  useEffect(() => {
    const currentKey = selectedVacancyId ?? null;
    if (initialLoadDoneRef.current === currentKey) return;
    if (viewMode !== 'search') return;
    if (
      selectedVacancyId &&
      (selectedVacancyLoading || !selectedVacancy || selectedVacancy.id !== selectedVacancyId)
    ) {
      return;
    }
    if (!searchSourcesLoaded || sourceNeedsNormalization) {
      return;
    }
    const pendingVacancyFilters = vacancyFiltersJustAppliedRef.current;
    if (pendingVacancyFilters) {
      const currentFiltersKey = getFiltersStateKey(filters as any);

      if (
        pendingVacancyFilters.vacancyId === currentKey &&
        pendingVacancyFilters.filtersKey !== currentFiltersKey
      ) {
        return;
      }

      vacancyFiltersJustAppliedRef.current = null;
    }
    initialLoadDoneRef.current = currentKey;

    try {
      const storage = selectedVacancyId ? localStorage : sessionStorage;
      const cacheKey = selectedVacancyId ? `searchResultsCache_v${selectedVacancyId}` : 'searchResultsCache';
      const raw = storage.getItem(cacheKey);
      
      if (raw) {
        const parsed = JSON.parse(raw);
        // TASKNEIROKLYUCH-645: filters.limit теперь управляет backend per_page,
        // поэтому разные limit → разные ответы бэка → кеш не валиден.
        const filtersMatch = JSON.stringify(parsed.filters) === JSON.stringify(filters);
        // Remove time check for vacancy
        const isFresh = selectedVacancyId ? true : (Date.now() - parsed.timestamp) < 5 * 60 * 1000;
        if (filtersMatch && isFresh && parsed.results) {
          setSearchData(parsed.results);
          setIsCachedRelevantSearchResult(!!parsed.isRelevantFiltered);
          setCachedRelevancePreset(parsed.relevancePreset || null);
          // Восстанавливаем курсоры джамперов из кэша
          if (parsed.jumperCursors) {
            jumperCursors.current = parsed.jumperCursors;
          }
          if (parsed.avitoCursors) {
            avitoCursors.current = parsed.avitoCursors;
          }
          return;
        }
      }
    } catch {}

    // Авто-запрос только если нет валидного кэша
    handleSearch({ resetJumperCursors: true });
  }, [viewMode, filters, handleSearch, searchSourcesLoaded, selectedRelevance, selectedVacancy, selectedVacancyId, selectedVacancyLoading, sourceNeedsNormalization]);



  // Sync admin view from URL query parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const adminParam = params.get('admin');
    const tabParam = params.get('tab');
    
    // Если есть параметры поиска, оставляем режим поиска (TASKNEIROKLYUCH-790: + expectedPositionName)
    const hasSearchParams = params.get('searchMode') || params.get('positionName') || params.get('expectedPositionName');
    if (hasSearchParams) {
      logInfo('Search parameters detected, keeping search mode');
      return;
    }

    // HH OAuth персонален и доступен любой авторизованной роли.
    if (adminParam === 'settings-hh') {
      safeSetViewMode('hh-auth');
      return;
    }

    // Если нет прав администратора — игнорируем любые admin-параметры и выходим
    if (!canAccessAdminViews) {
      if (adminParam) {
        logInfo('Non-superadmin attempted to open admin view via URL; ignoring');
        // Чистим URL от admin
        params.delete('admin');
        params.delete('tab');
        navigate({ pathname: '/recruiter/search', search: params.toString() ? `?${params.toString()}` : '' }, { replace: true });
      }
      return;
    }

    if (!adminParam) return; // do not override when not present
    if (adminParam === 'panel') safeSetViewMode('admin-panel');
    else if (adminParam === 'settings') safeSetViewMode('hh-auth');
    else if (adminParam === 'settings-ats') safeSetViewMode('ats-settings');
    else if (adminParam === 'statistics' || adminParam === 'stats') {
      if (tabParam === 'activity') safeSetViewMode('activity');
      else safeSetViewMode('statistics');
    }
    else if (adminParam === 'activity') safeSetViewMode('activity');
    else if (adminParam === 'vacancies') safeSetViewMode('vacancies');
  }, [location.search, canAccessAdminViews, navigate, safeSetViewMode]);

  // Log component lifecycle
  useEffect(() => {
    logInfo('RelevanterPage mounted');
    return () => logInfo('RelevanterPage unmounted');
  }, []);

  // Listen for HH settings open event from ResultsTable
  useEffect(() => {
    const handleOpenHHSettings = () => {
      setViewMode('hh-auth');
      logInfo('Opening HH Auth (was Settings) from error dialog');
    };

    window.addEventListener('openHHSettings', handleOpenHHSettings);
    return () => window.removeEventListener('openHHSettings', handleOpenHHSettings);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (viewMode !== 'search') return;
      if (!token) return;
      if (
        !searchSourcesLoaded
        || sourceNeedsNormalization
        || hasNoAvailableSearchSources
        || filters.source !== 'hh'
        || !hasAvailableSource(searchSources, 'hh')
      ) {
        setHhAuthRequired(false);
        return;
      }
      try {
        const status = await checkHHStatus();
        if (cancelled) return;
        const unauthorized = status?.status === 'unauthorized';
        setHhAuthRequired(unauthorized);
        if (unauthorized) {
          setSearchSources((prev) => (
            Array.isArray(prev) ? setSourceAvailable(prev, 'hh', false) : prev
          ));
        }
      } catch {
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [
    filters.source,
    hasNoAvailableSearchSources,
    searchSources,
    searchSourcesLoaded,
    sourceNeedsNormalization,
    token,
    viewMode,
  ]);

  return (
    <div className="bg-[#e9eae2] relative h-full overflow-visible">
      <QueryClientProvider client={queryClient}>
        <div className="relative h-full overflow-visible">
          <div className="relative h-full overflow-visible">
            {/* Центрирующий контейнер для контента */}
            <div className="w-full h-full">
              <div className="flex flex-col gap-6 w-full h-full">
                {/* Main Content - единый контейнер как в Figma */}
                <div className="bg-[#f5f6f1] box-border content-stretch flex flex-col gap-[10px] items-start justify-start relative shrink-0 w-full flex-1 h-full overflow-visible">
                  <div
                    ref={scrollRef}
                    className={
                      viewMode === 'search' && searchTab === 'responses'
                        ? 'w-full min-h-full mt-[1px] p-[16px] flex flex-col'
                        : 'w-full h-full mt-[1px] overflow-y-auto p-[16px] flex flex-col'
                    }
                  >
                    {viewMode === 'search' ? (
                        <div className="flex flex-col w-full flex-1">
                          {/* Баннер «Требуется авторизация в HH.ru» — НЕ блокирует UI:
                              SourceTabs внутри SearchInterface остаётся доступен,
                              пользователь может либо авторизоваться, либо переключить source. */}
                          {hhAuthRequired && filters.source === 'hh' && (
                            <div className="px-2 mb-2">
                              <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div>
                                  <div className="text-yellow-900 text-base font-semibold">
                                    Требуется авторизация в HH.ru
                                  </div>
                                  <div className="text-yellow-800 text-sm mt-1">
                                    Авторизуйтесь, чтобы продолжить поиск резюме на HH, или выберите другой источник выше.
                                  </div>
                                </div>
                                <button
                                  onClick={() => {
                                    safeSetViewMode('hh-auth');
                                    navigate('/recruiter/search?admin=settings-hh', { replace: true });
                                  }}
                                  className="text-white font-medium px-4 py-2 rounded-xl transition-colors shrink-0 self-start sm:self-auto"
                                  style={{ backgroundColor: colors.primary }}
                                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.primaryHover; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = colors.primary; }}
                                >
                                  Авторизация
                                </button>
                              </div>
                            </div>
                          )}
                          {selectedVacancy && (
                            <div className="px-2 mb-2">
                              <SearchFromSourcesHeader
                                source={filters.source || 'hh'}
                                onHHSettingsClick={() => safeSetViewMode('hh-auth')}
                                onHHAuthClick={() => safeSetViewMode('hh-auth')}
                                onATSSettingsClick={() => safeSetViewMode('ats-settings')}
                                tabToggle={
                                  <div className="flex items-center gap-2">
                                    {searchTab === 'responses' && (
                                      <SourceTabs
                                        dynamic
                                        sources={searchSources}
                                        activeSource={filters.source || 'hh'}
                                        onSourceChange={(source) => handleSourceSwitch(source)}
                                      />
                                    )}
                                    <ContentTabToggle
                                      options={[
                                        { id: 'search' as const, label: filters.source === 'hh' ? 'База hh' : 'Поиск' },
                                        ...(
                                          filters.source === 'hh' && ((selectedVacancy as any)?.hhVacancyId || (selectedVacancy as any)?.hhAdditionalVacancyIds?.length > 0)
                                            ? [{ id: 'responses' as const, label: 'Отклики hh' }]
                                            : []
                                        ),
                                        ...(
                                          filters.source === 'avito' && (selectedVacancy as any)?.avitoVacancyId
                                            ? [{ id: 'responses' as const, label: 'Отклики Avito' }]
                                            : []
                                        ),
                                      ]}
                                      activeTab={searchTab}
                                      onTabChange={(tab) => setSearchTab(tab)}
                                    />
                                  </div>
                                }
                              />
                            </div>
                          )}

                          {searchTab === 'search' ? (
                            <>
                              <SearchInterface
                                hideHeader
                                filters={filters}
                                onFiltersChange={handleFilterChange}
                                onSearch={() => handleSearch({ resetJumperCursors: true })}
                                searchValue={filters.positionName || ''}
                                onSearchChange={handleSearchValueChange}
                                searchTitle={vacancyTitle || 'Резюме на должность менеджер по лизиргу'}
                                candidateCount={getSearchInterfaceCandidateCount(searchData)}
                                isApproximate={searchData?.isApproximate}
                                totalFound={searchData?.totalFound}
                                filteredCount={searchData?.filteredCount}
                                searchMode={searchMode}
                                onSearchModeChange={(mode) => setSearchMode(mode === 'vacancy' ? 'vacancy' : 'custom')}
                                shouldAutoParse={shouldAutoParse}
                                parsedSummary={parsedSummary}
                                setParsedSummary={setParsedSummary}
                                onHHSettingsClick={() => safeSetViewMode('hh-auth')}
                                onHHAuthClick={() => safeSetViewMode('hh-auth')}
                                onATSSettingsClick={() => safeSetViewMode('ats-settings')}
                                onHHVacanciesClick={() => safeSetViewMode('vacancies')}
                                isModalSearchActive={isModalSearchActive}
                                setIsModalSearchActive={setIsModalSearchActive}
                                selectedRelevance={displayRelevance}
                                onRelevanceChange={handleRelevanceChange}
                                relevancePageSize={relevancePageSize}
                                onRelevancePageSizeChange={handleRelevancePageSizeChange}
                                showRelevanceReset={!!displayRelevance || isCachedRelevantSearchResult}
                                onRelevanceReset={() => handleRelevanceChange(null)}
                                scoringEnabled={(filters as any).scoringEnabled !== false}
                                onScoringEnabledChange={(enabled) => handleFilterChange({ scoringEnabled: enabled } as Partial<FiltersState>)}
                                sourceDropdown={
                                  <SourceTabs
                                    dynamic
                                    sources={searchSources}
                                    activeSource={filters.source || 'hh'}
                                    onSourceChange={(source) => handleSourceSwitch(source)}
                                  />
                                }
                              />
                              {(loading || loadingMore || searchData || error) && (
                                <ResultsCard
                                  data={searchData}
                                  loading={loading}
                                  loadingMore={loadingMore}
                                  error={error}
                                  filters={filters}
                                  onPageChange={handlePageChange}
                                  onToggleFilters={() => setFiltersVisible((v) => !v)}
                                  filtersVisible={filtersVisible}
                                  onSearch={() => handleSearch({ resetJumperCursors: true })}
                                  vacancyTitle={vacancyTitle}
                                  sourcePositionId={sourcePositionId}
                                  selectedRelevance={displayRelevance}
                                  searchProgress={searchProgress}
                                  onCancelSearch={activeSearchId ? handleCancelActiveSearch : undefined}
                                  onAiScoresChange={handleSearchAiScoresChange}
                                />
                              )}
                            </>
                          ) : (
                            <ResponsesTab
                                selectedVacancy={selectedVacancy}
                                hhVacancyNames={hhVacancyNames}
                                allResponsesData={responsesData}
                                responsesData={(filters.source || 'hh') === 'avito'
                                  ? responsesData
                                  : responsesData.slice(
                                    (responsesCurrentPage - 1) * responsesPerPage,
                                    responsesCurrentPage * responsesPerPage
                                  )}
                                responsesLoading={responsesLoading}
                                responsesRelevanceLoading={responsesRelevanceLoading}
                                searchProgress={searchProgress}
                                onCancelSearch={activeSearchId ? handleCancelActiveSearch : undefined}
                                responsesRelevanceApplied={responsesRelevanceApplied}
                                relevancePreset={displayResponsesRelevancePreset}
                                relevanceCount={responsesRelevanceTargetCount}
                                onRelevancePresetChange={setResponsesRelevancePreset}
                                onRelevanceCountChange={setResponsesRelevanceTargetCount}
                                aiEvaluations={aiEvaluations}
                                evaluatingIds={evaluatingIds}
                                scoringStats={responsesScoringStats}
                                vacancyScoringEnabled={vacancyScoringEnabled && (filters.source || 'hh') === 'hh'}
                                responsesSource={filters.source || 'hh'}
                                onEvaluateResume={['hh', 'avito'].includes(filters.source || 'hh') ? evaluateResponseResume : undefined}
                                onRefresh={() => loadResponses(true, true)}
                                onRemoveResponses={removeResponses}
                                onRelevanceClear={() => clearRelevantResponses()}
                                selectedResponses={selectedResponses}
                                onSelectedResponsesChange={setSelectedResponses}
                                responsesLoadLimit={responsesLoadLimit}
                                onResponsesLoadLimitChange={setResponsesLoadLimit}
                                parseMode={responsesParseMode}
                                onParseModeChange={setResponsesParseMode}
                                searchValue={responsesFilters.searchText}
                                onSearchValueChange={(v) => updateResponsesFilters({ searchText: v })}
                                filters={responsesFilters}
                                onFiltersChange={updateResponsesFilters}
                                onSearch={() => handleResponsesSearch()}
                                onClearFilters={handleClearResponsesFilters}
                                scoringCreditLimitHit={scoringCreditLimitHit}
                                pagination={{
                                  currentPage: responsesCurrentPage,
                                  perPage: responsesPerPage,
                                  totalItems: responsesFound || responsesData.length,
                                  totalPages: Math.ceil(((responsesFound || responsesData.length) || 0) / Math.max(1, responsesPerPage)),
                                  onPageChange: (nextPage: number) => {
                                    setResponsesCurrentPage(nextPage);
                                    if ((filters.source || 'hh') === 'avito') {
                                      void loadResponses(true, true, nextPage, responsesPerPage);
                                    }
                                  },
                                  onPerPageChange: (nextPerPage: number) => {
                                    setResponsesPerPage(nextPerPage);
                                    setResponsesCurrentPage(1);
                                    if ((filters.source || 'hh') === 'avito') {
                                      void loadResponses(true, true, 1, nextPerPage);
                                    }
                                  }
                                }}
                            />
                          )}
                        </div>
                    ) : viewMode === 'admin-panel' ? (
                      <AdminPanelCard
                        onBackToSearch={() => { safeSetViewMode('search'); navigate('/recruiter/search', { replace: true }); }}
                        onOpenSettings={() => safeSetViewMode('hh-settings')}
                        onOpenStatistics={() => safeSetViewMode('statistics')}
                      />
                    ) : viewMode === 'hh-settings' ? (
                      filters.source === 'hh' ? (
                        <HHAuthCard
                          onBackToSearch={() => safeSetViewMode('search')}
                        />
                      ) : (
                        <EStaffAuthCard
                          onBackToSearch={() => safeSetViewMode('search')}
                        />
                      )
                    ) : viewMode === 'statistics' ? (
                      <StatisticsCard
                        onBackToSearch={() => { safeSetViewMode('search'); navigate('/recruiter/search', { replace: true }); }}
                        onOpenAdminPanel={() => safeSetViewMode('admin-panel')}
                        onOpenActivity={() => { safeSetViewMode('activity'); navigate('/recruiter/search?admin=stats&tab=activity', { replace: true }); }}
                      />
                    ) : viewMode === 'activity' ? (
                      <ActivityCard
                        onBackToSearch={() => { safeSetViewMode('search'); navigate('/recruiter/search', { replace: true }); }}
                        onOpenAdminPanel={() => safeSetViewMode('admin-panel')}
                        onOpenStatistics={() => { safeSetViewMode('statistics'); navigate('/recruiter/search?admin=stats', { replace: true }); }}
                      />
                    ) : viewMode === 'hh-auth' ? (
                      filters.source === 'hh' ? (
                        <HHAuthCard
                          onBackToSearch={() => safeSetViewMode('search')}
                        />
                      ) : (
                        <EStaffAuthCard
                          onBackToSearch={() => safeSetViewMode('search')}
                        />
                      )
                    ) : viewMode === 'ats-settings' ? (
                      <EStaffAuthCard
                        onBackToSearch={() => safeSetViewMode('search')}
                      />
                    ) : viewMode === 'vacancies' ? (
                      <VacanciesCard
                        onBackToSearch={() => safeSetViewMode('search')}
                      />
                    ) : (
                      <>
                        {/* Верхняя карточка - информация о поиске */}
                        <SearchInfoCard
                          filters={filters}
                          searchData={searchData}
                          loading={loading}
                          setViewMode={safeSetViewMode}
                        />
                        
                        {/* Нижняя карточка - результаты поиска */}
                        <ResultsCard
                          data={searchData}
                          loading={loading}
                          error={error}
                          filters={filters}
                          onPageChange={handlePageChange}
                          onToggleFilters={() => setFiltersVisible((v) => !v)}
                          filtersVisible={filtersVisible}
                          onSearch={handleSearch}
                          vacancyTitle={vacancyTitle}
                          sourcePositionId={sourcePositionId}
                          selectedRelevance={displayRelevance}
                          searchProgress={searchProgress}
                          onCancelSearch={activeSearchId ? handleCancelActiveSearch : undefined}
                          onAiScoresChange={handleSearchAiScoresChange}
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <ScrollToTopButton target={scrollRef} />
      </QueryClientProvider>
    </div>
  );
};

export default RelevanterPage;
